/* ============================================================
   DietXP IC Calculator - Clinical Engine
   All calculations client-side. Liquid provides shell only.
   Clinical accuracy: ICMR-NIN 2020 · ESPEN ICU 2023 · ASPEN/SCCM
   ============================================================ */

(function() {
'use strict';

/* ── 1. EQUATION LIBRARY ────────────────────────────────── */
var EQ = {

  /* Weir - Abbreviated (VO2 & VCO2 in L/min) */
  weirAbbreviated: function(vo2, vco2) {
    return ((3.941 * vo2) + (1.106 * vco2)) * 1440;
  },

  /* Weir - Complete (VO2, VCO2 in L/min; UN in g/day)
     UN is already per-day - subtract AFTER the 1440 conversion,
     otherwise the correction is multiplied by 1440 and turns hugely negative. */
  weirComplete: function(vo2, vco2, UN) {
    return ((3.941 * vo2) + (1.106 * vco2)) * 1440 - (2.17 * UN);
  },

  /* VCO2-only (VCO2 in mL/min) - ICU ventilator mode.
     Derived from the abbreviated Weir equation with VO2 estimated as VCO2 / RQ:
       REE kcal/day = VCO2(mL/min) x multiplier(RQ)
       multiplier(RQ) = [(3.941 / RQ) + 1.106] x 1.44
     The multiplier is recomputed continuously from the selected RQ - no fixed
     constant. The commonly cited 8.19 corresponds to RQ ~= 0.86 (NOT 0.85: at
     RQ 0.85 the multiplier is ~= 8.27). [#1 accuracy fix] */
  vcoMultiplier: function(rq) {
    rq = rq || 0.86;
    return ((3.941 / rq) + 1.106) * 1.44;
  },
  vco2Only: function(vco2_mLmin, rq) {
    return vco2_mLmin * EQ.vcoMultiplier(rq);
  },

  /* Mifflin-St Jeor 1990 (W kg, H cm, A yr, sex M/F) */
  mifflinStJeor: function(W, H, A, sex) {
    var base = (10 * W) + (6.25 * H) - (5 * A);
    return sex === 'M' ? base + 5 : base - 161;
  },

  /* Revised Harris-Benedict 1984 */
  harrisBenedict: function(W, H, A, sex) {
    if (sex === 'M') {
      return 88.362 + (13.397 * W) + (4.799 * H) - (5.677 * A);
    }
    return 447.593 + (9.247 * W) + (3.098 * H) - (4.330 * A);
  },

  /* Henry Oxford 2005 (W kg, H cm - NOTE: Henry uses cm not m) */
  henryOxford: function(W, H, A, sex) {
    if (sex === 'M') {
      if (A < 30)  return (14.4 * W) + (3.13 * H) + 113;
      if (A < 60)  return (11.4 * W) + (5.41 * H) - 137;
      return             (11.4 * W) + (5.41 * H) - 256;
    } else {
      if (A < 30)  return (10.4 * W) + (6.15 * H) - 282;
      if (A < 60)  return (8.18 * W) + (5.02 * H) - 11.6;
      return             (8.52 * W) + (4.21 * H) + 10.7;
    }
  },

  /* ICMR-NIN 2020: Schofield weight-only base × correction */
  icmrNIN2020: function(W, A, sex) {
    var base;
    if (sex === 'M') {
      if (A < 30)       base = (15.057 * W) + 692.2;
      else if (A < 60)  base = (11.472 * W) + 873.1;
      else              base = (11.711 * W) + 587.7;
      return base * 0.90; /* -10% correction */
    } else {
      if (A < 30)       base = (14.818 * W) + 486.6;
      else if (A < 60)  base = (8.126 * W) + 845.6;
      else              base = (9.082 * W) + 658.5;
      return base * 0.91; /* -9% correction */
    }
  },

  /* Schofield W+H - Pediatric (W kg, H metres) */
  schofieldPediatric: function(W, Hm, A, sex) {
    if (sex === 'M') {
      if (A < 3)   return (0.167 * W) + (1517.4 * Hm) - 617.6;
      if (A < 10)  return (19.6 * W) + (130.3 * Hm) + 414.9;
      if (A < 18)  return (16.25 * W) + (137.2 * Hm) + 515.5;
    } else {
      if (A < 3)   return (16.25 * W) + (1023.2 * Hm) - 413.5;
      if (A < 10)  return (16.97 * W) + (161.8 * Hm) + 371.2;
      if (A < 18)  return (8.365 * W) + (465 * Hm) + 200;
    }
    return null;
  },

  /* Cunningham 1980 (LBM kg) */
  cunningham: function(LBM) {
    return 500 + (22 * LBM);
  },

  /* Katch-McArdle 1996 (LBM kg) */
  katchMcArdle: function(LBM) {
    return 370 + (21.6 * LBM);
  },

  /* Schofield Weight-Only (adult, no height correction) - item 11 */
  schofieldWeightOnly: function(W, A, sex) {
    if (sex === 'M') {
      if (A < 30)       return (15.057 * W) + 692.2;
      else if (A < 60)  return (11.472 * W) + 873.1;
      else              return (11.711 * W) + 587.7;
    } else {
      if (A < 30)       return (14.818 * W) + 486.6;
      else if (A < 60)  return (8.126 * W) + 845.6;
      else              return (9.082 * W) + 658.5;
    }
  }
};

/* The DOM-bound engine consumes the same pure rules used by the regression
   tests. Keep the inline library as a no-network fallback for legacy embeds. */
var CLINICAL_RULES = window.DietXPClinicalRules || null;
if (CLINICAL_RULES && CLINICAL_RULES.equations) EQ = CLINICAL_RULES.equations;
var ALGORITHM_VERSION = CLINICAL_RULES ? CLINICAL_RULES.version : '3.2.0-preclinical';
var GUIDELINE_REVIEW_DATE = CLINICAL_RULES ? CLINICAL_RULES.reviewDate : '2026-06-21';

/* ── 2. BODY COMPOSITION HELPERS ────────────────────────── */
function computeBMI(W, Hcm) {
  if (CLINICAL_RULES) return CLINICAL_RULES.anthropometry.bmi(W, Hcm);
  var Hm = Hcm / 100;
  return W / (Hm * Hm);
}

function bmiCategory(bmi, region) {
  var protocol = val('mep-bmi-protocol') || 'auto';
  var useAsianIndian = protocol === 'asian-indian' || (protocol === 'auto' && region === 'india');
  if (protocol === 'local') return 'Local protocol — review classification';
  if (useAsianIndian) {
    if (bmi < 18.5) return 'Underweight';
    if (bmi < 23)   return 'Normal (India cutoff)';
    if (bmi < 25)   return 'Overweight at risk';
    if (bmi < 30)   return 'Obese Class I';
    return 'Obese Class II';
  }
  if (bmi < 18.5) return 'Underweight';
  if (bmi < 25)   return 'Normal';
  if (bmi < 30)   return 'Overweight';
  if (bmi < 35)   return 'Obese Class I';
  return 'Obese Class II+';
}

function computeIBW(Hcm, sex) {
  var protocolNode = document.getElementById('mep-ibw-protocol');
  var overrideNode = document.getElementById('mep-ibw-override');
  var overrideValue = overrideNode ? parseFloat(overrideNode.value) : 0;
  if (protocolNode && protocolNode.value === 'local' && overrideValue > 0) return overrideValue;
  if (CLINICAL_RULES) return CLINICAL_RULES.anthropometry.devineIBW(Hcm, sex);
  var Hin = Hcm / 2.54;
  return sex === 'M' ? 50 + 2.3 * (Hin - 60) : 45.5 + 2.3 * (Hin - 60);
}

function computeAdjBW(actual, IBW) {
  var factorNode = document.getElementById('mep-adjbw-factor');
  var factor = factorNode ? parseFloat(factorNode.value) : 0.25;
  if (!isFinite(factor)) factor = 0.25;
  return CLINICAL_RULES ? CLINICAL_RULES.anthropometry.adjustedWeight(actual, IBW, factor) : IBW + factor * (actual - IBW);
}

function buildWorkedCalculation(meta) {
  function n(value, digits) { return Number(value).toFixed(digits == null ? 1 : digits); }
  var base = '';
  if (meta.tier === 1) {
    base = '[(3.941 × ' + n(meta.vo2, 3) + ') + (1.106 × ' + n(meta.vco2, 3) + ')] × 1440';
    if (meta.un > 0) base += ' − (2.17 × ' + n(meta.un, 2) + ')';
  } else if (meta.vco2mL > 0) {
    var multiplier = EQ.vcoMultiplier(meta.rq);
    base = 'Multiplier = [(3.941 ÷ ' + n(meta.rq, 2) + ') + 1.106] × 1.44 = ' + n(multiplier, 3) + '; ' + n(meta.vco2mL, 0) + ' × ' + n(multiplier, 3);
  } else if (meta.vo2Only > 0) {
    var estimatedVco2 = meta.vo2Only * meta.rqVo2;
    base = 'Estimated VCO₂ = ' + n(meta.vo2Only, 3) + ' × ' + n(meta.rqVo2, 2) + ' = ' + n(estimatedVco2, 3) + ' L/min; [(3.941 × ' + n(meta.vo2Only, 3) + ') + (1.106 × ' + n(estimatedVco2, 3) + ')] × 1440';
  } else if (meta.method.indexOf('Body Composition Mean') === 0) {
    base = '[(500 + 22 × ' + n(meta.lbm, 1) + ') + (370 + 21.6 × ' + n(meta.lbm, 1) + ')] ÷ 2';
  } else if (meta.method.indexOf('ICMR-NIN') === 0) {
    var icmrA, icmrB, correction;
    if (meta.sex === 'M') {
      if (meta.age < 30) { icmrA = 15.057; icmrB = 692.2; }
      else if (meta.age < 60) { icmrA = 11.472; icmrB = 873.1; }
      else { icmrA = 11.711; icmrB = 587.7; }
      correction = 0.90;
    } else {
      if (meta.age < 30) { icmrA = 14.818; icmrB = 486.6; }
      else if (meta.age < 60) { icmrA = 8.126; icmrB = 845.6; }
      else { icmrA = 9.082; icmrB = 658.5; }
      correction = 0.91;
    }
    base = '[(' + icmrA + ' × ' + n(meta.weight, 1) + ') + ' + icmrB + '] × ' + correction;
  } else if (meta.method.indexOf('Henry Oxford') === 0) {
    var a, b, c;
    if (meta.sex === 'M') {
      if (meta.age < 30) { a = 14.4; b = 3.13; c = 113; }
      else if (meta.age < 60) { a = 11.4; b = 5.41; c = -137; }
      else { a = 11.4; b = 5.41; c = -256; }
    } else {
      if (meta.age < 30) { a = 10.4; b = 6.15; c = -282; }
      else if (meta.age < 60) { a = 8.18; b = 5.02; c = -11.6; }
      else { a = 8.52; b = 4.21; c = 10.7; }
    }
    base = '(' + a + ' × ' + n(meta.weight, 1) + ') + (' + b + ' × ' + n(meta.height, 1) + ') ' + (c < 0 ? '− ' + Math.abs(c) : '+ ' + c);
  } else if (meta.method.indexOf('Schofield') === 0) {
    var hm = meta.height / 100;
    if (meta.sex === 'M' && meta.age < 3) base = '(0.167 × ' + n(meta.weight, 1) + ') + (1517.4 × ' + n(hm, 2) + ') − 617.6';
    else if (meta.sex === 'M' && meta.age < 10) base = '(19.6 × ' + n(meta.weight, 1) + ') + (130.3 × ' + n(hm, 2) + ') + 414.9';
    else if (meta.sex === 'M') base = '(16.25 × ' + n(meta.weight, 1) + ') + (137.2 × ' + n(hm, 2) + ') + 515.5';
    else if (meta.age < 3) base = '(16.25 × ' + n(meta.weight, 1) + ') + (1023.2 × ' + n(hm, 2) + ') − 413.5';
    else if (meta.age < 10) base = '(16.97 × ' + n(meta.weight, 1) + ') + (161.8 × ' + n(hm, 2) + ') + 371.2';
    else base = '(8.365 × ' + n(meta.weight, 1) + ') + (465 × ' + n(hm, 2) + ') + 200';
  } else {
    base = '(10 × ' + n(meta.weight, 1) + ') + (6.25 × ' + n(meta.height, 1) + ') − (5 × ' + n(meta.age, 1) + ') ' + (meta.sex === 'M' ? '+ 5' : '− 161');
  }
  if (meta.stress > 1 && meta.tier === 3) base = '(' + base + ') × ' + n(meta.stress, 2);
  return base + ' = ' + Math.round(meta.ree).toLocaleString('en-IN') + ' kcal/day';
}

/* Boer formula LBM estimate */
function estimateLBM(W, Hcm, sex) {
  return sex === 'M'
    ? (0.407 * W) + (0.267 * Hcm) - 19.2
    : (0.252 * W) + (0.473 * Hcm) - 48.3;
}

/* ── 3. PAL LOOKUP ──────────────────────────────────────── */
var PAL_TABLE = {
  inpatient_bedbound: 1.00,
  sedentary_global: 1.20,
  light_global: 1.375,
  moderate_global: 1.55,
  active_global: 1.725,
  very_active_global: 1.90,
  sedentary_india: 1.40,
  moderate_india: 1.8,   /* ICMR-NIN 2020 moderate work (Annexure A1: 2710 / 2130 kcal at 65 / 55 kg) */
  heavy_india: 2.3,   /* ICMR-NIN 2020 heavy work (A1: 3470 / 2720 kcal) */
  icu: 1.00 /* Intubated / fully bedbound ICU - PAL 1.00 = REE only */
};

var PAL_LABELS = {
  inpatient_bedbound: 'Inpatient / Bedbound (×1.00)',
  sedentary_global: 'Sedentary (×1.20)',
  light_global: 'Light Activity (×1.375)',
  moderate_global: 'Moderate (×1.55)',
  active_global: 'Active (×1.725)',
  very_active_global: 'Very Active (×1.90)',
  sedentary_india: 'Sedentary - ICMR (×1.40)',
  moderate_india: 'Moderate Work - ICMR (×1.8)',
  heavy_india: 'Heavy Work - ICMR (×2.3)',
  icu: 'ICU Intubated / Bedbound (×1.00)'
};

/* ── 4. SPECIAL CONDITION ADDITIONS ─────────────────────── */
var PREG_KCAL = {
  none: { india: 0, global: 0 },
  t1:   { india: 0, global: 0 },
  t2:   { india: 350, global: 345 },
  t3:   { india: 350, global: 475 }
};

var LACT_KCAL = {
  none:  { india: 0, global: 0 },
  early: { india: 600, global: 550 },
  late:  { india: 520, global: 500 }
};

/* ── 5. PROTEIN TARGETS ─────────────────────────────────── */
var PROTEIN_TARGETS = {
  ambulatory:           { min: 0.8,  rec: '0.8–1.0',   src: 'WHO / RDA' },
  icu:                  { min: 1.3,  rec: '1.3',       src: 'ESPEN ICU 2023 — deliver progressively' },
  icu_espen:            { min: 1.3,  rec: '1.3',       src: 'ESPEN ICU 2023 — deliver progressively' },
  icu_aspen:            { min: 1.2,  rec: '1.2–2.0',   src: 'ASPEN/SCCM adult critical care guidance' },
  /* ICU-obese tiers: protein dosed on IBW, ASPEN/SCCM 2022 critical care obesity guidance.
     BMI 30–39.9 → 2.0 g/kg IBW/day; BMI ≥40 → 2.5 g/kg IBW/day. */
  icu_obese:            { min: 2.0,  rec: '2.0–2.5',   src: 'ASPEN/SCCM 2016 critical care obesity (BMI 30–39.9)' },
  icu_obese_morbid:     { min: 2.5,  rec: '2.5',       src: 'ASPEN/SCCM 2016 critical care obesity (BMI ≥40)' },
  oncology:             { min: 1.0,  rec: '1.0–1.5',   src: 'ESPEN Oncology 2021 · IAPEN India' },
  oncology_sarcopenia:  { min: 1.2,  rec: '1.2–1.5',   src: 'ESPEN Oncology 2021 · IAPEN India' },
  oncology_cachexia:    { min: 1.2,  rec: '1.2–1.5',   src: 'ESPEN Cancer 2021 — individualise to catabolic state' },
  /* ESPEN Geriatric 2022: ≥1.0 healthy; 1.2–1.5 with acute illness/morbidity.
     rec spans the full range; flag advises 1.5 upper end for active morbidity. */
  geriatric:            { min: 1.0,  rec: '1.0–1.5',   src: 'ESPEN Geriatric 2022' },
  /* KDOQI 2020: metabolically stable MHD/PD 1.0–1.2 g/kg ideal body weight/day. */
  renal:                { min: 0.6,  rec: '0.6–0.8',   src: 'KDOQI 2020 CKD non-dialysis (LPD approach)' },
  renal_dialysis:       { min: 1.0,  rec: '1.0–1.2',   src: 'KDOQI 2020 — stable MHD/PD, ideal body weight' },
  renal_aki:            { min: 0.8,  rec: '0.8–1.0',   src: 'ESPEN Kidney 2021 — AKI without acute/critical illness' },
  renal_aki_icu:        { min: 1.0,  rec: '1.0–1.3',   src: 'ESPEN Kidney 2021 — acute/critical illness, no KRT; advance if tolerated' },
  renal_dialysis_icu:   { min: 1.3,  rec: '1.3–1.5',   src: 'ESPEN Kidney 2021 — critical illness on intermittent KRT' },
  renal_crrt:           { min: 1.5,  rec: '1.5–1.7',   src: 'ESPEN Kidney 2021 — CKRT/PIKRT' },
  india_ambulatory:     { min: 0.83, rec: '0.83–1.0',  src: 'ICMR-NIN 2020' },
  /* Pediatric protein: WHO / ESPGHAN age-band targets (g/kg actual wt/day). */
  pediatric_infant:     { min: 2.0,  rec: '2.0–3.0',   src: 'WHO / ESPGHAN 0–1 yr' },
  pediatric_toddler:    { min: 1.5,  rec: '1.5–2.0',   src: 'WHO / ESPGHAN 1–3 yr' },
  pediatric_child:      { min: 1.2,  rec: '1.2–1.8',   src: 'WHO / ESPGHAN 4–12 yr' },
  pediatric_adolescent: { min: 1.0,  rec: '1.0–1.5',   src: 'WHO / ESPGHAN 13–17 yr' }
};

/* ── 6. MACRO PRESETS ───────────────────────────────────── */
var MACRO_PRESETS = {
  standard:    { p: 0.30, f: 0.30, c: 0.40 },
  high_protein:{ p: 0.35, f: 0.30, c: 0.35 },
  diabetic:    { p: 0.25, f: 0.35, c: 0.40 },
  renal:       { p: 0.20, f: 0.35, c: 0.45 }
};

/* ── 7. DOM HELPERS ─────────────────────────────────────── */
function el(id) { return document.getElementById(id); }
function val(id) { return el(id) ? el(id).value : ''; }
function numVal(id) { return parseFloat(val(id)) || 0; }
function selectedLabel(id) {
  var node = el(id);
  if (!node || node.selectedIndex < 0) return '';
  return node.options[node.selectedIndex].text;
}

function setHTML(id, html) {
  var node = el(id);
  if (node) node.innerHTML = html;
}

function setText(id, text) {
  var node = el(id);
  if (node) node.textContent = text;
}

function setResultTransparency(meta) {
  setText('ic-formula-method', meta.method || '-');
  setText('ic-formula-expression', meta.formula || '-');
  setText('ic-formula-inputs', meta.inputs || '-');
  setText('ic-formula-assumptions', meta.assumptions || '-');
  setText('ic-formula-target-phase', meta.targetPhase || 'No phase adjustment');
  setText('ic-formula-protein-basis', meta.proteinBasis || '-');
}

function setQualityStatus(quality) {
  var card = el('ic-quality-status-card');
  if (!card) return;
  card.className = 'ic-quality-status-card ic-quality-' + (quality.type || 'low');
  var scoreSuffix = typeof quality.score === 'number' ? ' · ' + quality.score + '/100' : '';
  setText('ic-quality-status-value', (quality.label || 'Low confidence') + scoreSuffix);
  setText('ic-quality-status-reason', quality.reason || 'Interpret with clinical judgement.');
  /* [#13] Explainable confidence — render the factor list */
  var factorsEl = el('ic-quality-factors');
  if (factorsEl) {
    if (quality.factors && quality.factors.length) {
      factorsEl.innerHTML = quality.factors.map(function(f){
        var cls = (f.level === 'good' || f.level === 'warn' || f.level === 'bad') ? f.level : '';
        return '<li class="' + cls + '">' + f.text + '</li>';
      }).join('');
      factorsEl.style.display = '';
    } else {
      factorsEl.innerHTML = '';
      factorsEl.style.display = 'none';
    }
  }
}

/* ── 8. TAB SYSTEM ──────────────────────────────────────── */
document.querySelectorAll('.ic-tab-btn').forEach(function(btn) {
  btn.addEventListener('click', function() {
    var tabId = this.getAttribute('data-tab');
    document.querySelectorAll('.ic-tab-btn').forEach(function(b) {
      b.classList.remove('active');
      b.setAttribute('aria-selected', 'false');
    });
    document.querySelectorAll('.ic-tab-panel').forEach(function(p) {
      p.classList.remove('active');
    });
    this.classList.add('active');
    this.setAttribute('aria-selected', 'true');
    var panel = el('tab-' + tabId);
    if (panel) panel.classList.add('active');
  });
});

/* [#18] Tablist keyboard navigation (ArrowLeft/Right/Home/End) */
(function(){
  var tabs = Array.prototype.slice.call(document.querySelectorAll('.ic-tab-btn'));
  tabs.forEach(function(tab, i){
    tab.addEventListener('keydown', function(e){
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      var idx = i;
      if (e.key === 'ArrowRight') idx = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') idx = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') idx = 0;
      else idx = tabs.length - 1;
      tabs[idx].focus();
      tabs[idx].click();
    });
  });
})();

/* ── 8b. IC MODE SWITCHER ────────────────────────────────── */
/* [#17] Disable inputs inside inactive mode sections so hidden fields leave the
   tab order and are ignored by assistive tech and browser validation. */
function syncModeFieldA11y() {
  document.querySelectorAll('.ic-mode-section').forEach(function(sec) {
    var active = sec.classList.contains('active');
    sec.setAttribute('aria-hidden', active ? 'false' : 'true');
    var fields = sec.querySelectorAll('input, select, textarea');
    Array.prototype.forEach.call(fields, function(f) { f.disabled = !active; });
  });
}
document.querySelectorAll('.ic-mode-btn').forEach(function(btn) {
  btn.addEventListener('click', function() {
    var mode = this.getAttribute('data-mode');
    document.querySelectorAll('.ic-mode-btn').forEach(function(b) { b.classList.remove('active'); b.setAttribute('aria-pressed', 'false'); });
    document.querySelectorAll('.ic-mode-section').forEach(function(s) { s.classList.remove('active'); });
    this.classList.add('active');
    this.setAttribute('aria-pressed', 'true');
    var section = el('mode-' + mode);
    if (section) section.classList.add('active');
    /* Quality panel: visible for gas pathways, hidden for pure predictive. */
    var qualityWrap = el('ic-quality-wrap');
    if (qualityWrap) qualityWrap.style.display = (mode === 'predictive') ? 'none' : '';
    syncModeFieldA11y();
    if (typeof updateLiveSummary === 'function') updateLiveSummary();
  });
});
/* Initialise aria-pressed + inactive-field state on load */
document.querySelectorAll('.ic-mode-btn').forEach(function(b) {
  b.setAttribute('aria-pressed', b.classList.contains('active') ? 'true' : 'false');
});
syncModeFieldA11y();

/* ── 9. LIVE BMI PREVIEW ────────────────────────────────── */
function updateBMIPreview() {
  var W = numVal('ic-weight');
  var H = numVal('ic-height');
  var region = val('ic-region');
  var bmiNode = el('bmi-preview');
  if (!bmiNode) return;
  if (!W || !H) {
    bmiNode.textContent = 'Enter weight and height in Patient Details to see BMI and AdjBW.';
    return;
  }
  var bmi = computeBMI(W, H);
  var cat = bmiCategory(bmi, region);
  var sex = val('ic-sex');
  var html = '<strong>BMI: ' + bmi.toFixed(1) + ' kg/m²</strong> - ' + cat;
  if (bmi >= 30 && sex && numVal('ic-age') >= 18) {
    var IBW = computeIBW(H, sex);
    if (W > IBW * 1.3) {
      var AdjBW = computeAdjBW(W, IBW);
      html += '<br><span style="color:#b45309;">Obese (BMI ≥30): IBW ' + IBW.toFixed(1) + ' kg · AdjBW ' + AdjBW.toFixed(1) + ' kg. ' + (val('ic-context') === 'icu' ? 'AdjBW is used for an ICU predictive energy equation.' : 'AdjBW is shown for reference; the selected non-ICU equation retains actual weight unless a specific protocol states otherwise.') + '</span>';
    }
  }
  bmiNode.innerHTML = html;
}

['ic-weight', 'ic-height', 'ic-region', 'ic-sex', 'ic-context'].forEach(function(id) {
  var node = el(id);
  if (node) node.addEventListener('input', updateBMIPreview);
});

/* ── 9b. LIVE VCO₂ MULTIPLIER PREVIEW (#1) ──────────────── */
function updateVcoMultPreview() {
  var node = el('ic-vco2-mult-live');
  if (!node) return;
  var rq = numVal('ic-rq') || 0.86;
  var mult = EQ.vcoMultiplier(rq);
  var vco2 = numVal('ic-vco2-mlmin');
  var txt = 'RQ ' + rq.toFixed(2) + ' → multiplier ×' + mult.toFixed(2);
  if (vco2 > 0) txt += '  ·  REE ≈ ' + Math.round(vco2 * mult).toLocaleString('en-IN') + ' kcal/day';
  node.innerHTML = txt;
}
['ic-rq', 'ic-vco2-mlmin'].forEach(function(id) {
  var node = el(id);
  if (node) node.addEventListener('input', updateVcoMultPreview);
});
updateVcoMultPreview();

/* ── 9c. LIVE WEIGHT-BASIS PREVIEW (#4) ─────────────────── */
function updateWeightBasis() {
  var W = numVal('ic-weight');
  var Hcm = numVal('ic-height');
  var sex = val('ic-sex');
  var dry = numVal('ic-dry-weight');
  var postDialysis = numVal('mep-post-dialysis-weight');
  var age = numVal('ic-age');
  var basis = val('ic-protein-weight-basis') || 'auto';
  var fmt = function(x){ return (x > 0 ? x.toFixed(1) + ' kg' : '—'); };
  var ibw = (Hcm > 0 && sex && age >= 18) ? computeIBW(Hcm, sex) : 0;
  var adj = (W > 0 && ibw > 0) ? computeAdjBW(W, ibw) : 0;
  setText('wb-actual', fmt(W));
  setText('wb-ibw', fmt(ibw));
  setText('wb-adj', fmt(adj));
  setText('wb-dry', fmt(dry));
  setText('wb-post-dialysis', fmt(postDialysis));
  ['actual','ideal','adjusted','dry','post-dialysis'].forEach(function(k){
    var cell = el('wb-cell-' + k);
    if (cell) cell.classList.remove('is-basis');
  });
  var map = { actual:'wb-cell-actual', ideal:'wb-cell-ideal', adjusted:'wb-cell-adjusted', dry:'wb-cell-dry', post_dialysis:'wb-cell-post-dialysis' };
  if (basis !== 'auto' && map[basis]) {
    var c = el(map[basis]);
    if (c) c.classList.add('is-basis');
  }
}
['ic-weight','ic-height','ic-sex','ic-age','ic-dry-weight','mep-post-dialysis-weight','ic-protein-weight-basis'].forEach(function(id) {
  var node = el(id);
  if (!node) return;
  node.addEventListener('input', updateWeightBasis);
  node.addEventListener('change', updateWeightBasis);
});
updateWeightBasis();

/* ── 9d. LIVE CLINICAL SUMMARY (#8) ─────────────────────── */
function lsMetric(k, v){ return '<div class="ic-live-metric"><div class="ic-live-metric-k">' + k + '</div><div class="ic-live-metric-v">' + v + '</div></div>'; }
function lsMetricFull(k, v){ return '<div class="ic-live-metric full"><div class="ic-live-metric-k">' + k + '</div><div class="ic-live-metric-v">' + v + '</div></div>'; }
function lsContextLabel(c){ var m = { ambulatory:'Outpatient', inpatient:'Inpatient ward', icu:'ICU', oncology:'Oncology', oncology_sarcopenia:'Oncology + Sarcopenia', oncology_cachexia:'Cachexia', geriatric:'Geriatric', renal:'Renal' }; return m[c] || c; }
function lsSuggestPathway(mode, region, A){
  if (mode && mode !== 'predictive') return 'Measured / gas-derived (Tier 1–2)';
  if (A > 0 && A < 18) return 'Schofield paediatric';
  if (region === 'india') return 'ICMR-NIN 2020 (India)';
  if (region === 'global_oxford') return 'Henry Oxford';
  if (region === 'body_comp') return 'Cunningham / Katch-McArdle (needs LBM)';
  return 'Mifflin-St Jeor';
}
function updateLiveSummary(){
  var stepsEl = el('ic-live-steps'), metricsEl = el('ic-live-metrics'), missingEl = el('ic-live-missing');
  if (!stepsEl || !metricsEl || !missingEl) return;
  var sex = val('ic-sex'), A = numVal('ic-age'), W = numVal('ic-weight'), Hcm = numVal('ic-height');
  var region = val('ic-region'), context = val('ic-context') || 'ambulatory';
  var modeBtn = document.querySelector('.ic-mode-btn.active');
  var mode = modeBtn ? modeBtn.getAttribute('data-mode') : 'predictive';
  var modeLabels = { 'predictive':'Predictive', 'full-ic':'Full IC', 'vco2-only':'VCO₂-only', 'vo2-only':'VO₂-only' };
  var missing = [];
  if (!sex) missing.push('sex');
  if (!(A >= 0.1)) missing.push('age');
  if (!(W >= 1)) missing.push('weight');
  if (!(Hcm >= 30)) missing.push('height');
  var detailsDone = missing.length === 0;
  var steps = [
    { n: 1, label: 'Patient details', done: detailsDone },
    { n: 2, label: 'Pathway: ' + (modeLabels[mode] || mode), done: true },
    { n: 3, label: 'Context: ' + lsContextLabel(context), done: true },
    { n: 4, label: 'Review', done: false }
  ];
  stepsEl.innerHTML = steps.map(function(s){
    return '<span class="ic-live-step' + (s.done ? ' done' : '') + '"><span class="ic-live-step-dot"></span>' + s.n + '. ' + s.label + '</span>';
  }).join('');
  var rows = '';
  if (W >= 1 && Hcm >= 30) {
    var bmi = computeBMI(W, Hcm);
    rows += lsMetric('BMI', bmi.toFixed(1) + ' kg/m²');
    rows += lsMetric('Class', bmiCategory(bmi, region));
    if (sex) {
      var ibw = computeIBW(Hcm, sex);
      rows += lsMetric('IBW (Devine)', ibw.toFixed(1) + ' kg');
      if (bmi >= 30 && W > ibw * 1.3 && A >= 18) rows += lsMetric('AdjBW', computeAdjBW(W, ibw).toFixed(1) + ' kg');
    }
  }
  rows += lsMetricFull('Suggested pathway', lsSuggestPathway(mode, region, A));
  if (mode === 'full-ic') rows += lsMetricFull('Needs', 'VO₂ + VCO₂ (L/min) + quality checks');
  else if (mode === 'vco2-only') rows += lsMetricFull('Needs', 'Ventilator VCO₂ (mL/min) + assumed RQ');
  else if (mode === 'vo2-only') rows += lsMetricFull('Needs', 'VO₂ (L/min) + assumed RQ');
  metricsEl.innerHTML = rows;
  if (detailsDone) {
    missingEl.className = 'ic-live-missing ready';
    missingEl.textContent = 'Required inputs complete — press Calculate Metabolic Profile.';
  } else {
    missingEl.className = 'ic-live-missing';
    missingEl.textContent = 'Still needed: ' + missing.join(', ') + '.';
  }
}
['ic-sex','ic-age','ic-weight','ic-height','ic-region','ic-context'].forEach(function(id){
  var node = el(id);
  if (!node) return;
  node.addEventListener('input', updateLiveSummary);
  node.addEventListener('change', updateLiveSummary);
});
updateLiveSummary();

/* ── 10. ACTIVITY CONTEXT TOGGLE ────────────────────────── */
el('ic-context').addEventListener('change', function() {
  var ctx = this.value;
  var wrap = el('activity-wrap');
  if (ctx === 'icu') {
    if (wrap) wrap.style.opacity = '0.4';
  } else {
    if (wrap) wrap.style.opacity = '1';
  }
});

/* ── 11. VALIDATE ───────────────────────────────────────── */
function validate() {
  var ok = true;
  var fields = [
    { id: 'ic-sex',    errId: 'err-sex',    check: function(v) { return v !== ''; } },
    { id: 'ic-age',    errId: 'err-age',    check: function(v) { return v >= 0.1 && v <= 120; } },
    { id: 'ic-weight', errId: 'err-weight', check: function(v) { return v >= 1 && v <= 300; } },
    { id: 'ic-height', errId: 'err-height', check: function(v) { return v >= 30 && v <= 250; } }
  ];
  fields.forEach(function(f) {
    var node = el(f.id);
    var errNode = el(f.errId);
    var v = node ? (node.tagName === 'SELECT' ? node.value : parseFloat(node.value)) : null;
    if (!f.check(v)) {
      if (node) node.classList.add('error');
      if (errNode) errNode.classList.add('visible');
      ok = false;
    } else {
      if (node) node.classList.remove('error');
      if (errNode) errNode.classList.remove('visible');
    }
  });

  /* ── Cross-field guards ── */
  var sexV      = val('ic-sex');
  var ageV      = parseFloat(val('ic-age')) || 0;
  var pregV     = val('ic-pregnancy') || 'none';
  var lactV     = val('ic-lactation') || 'none';
  var regionV   = val('ic-region');
  var rqV       = parseFloat(val('ic-rq')) || 0.86;
  var vco2mlV   = numVal('ic-vco2-mlmin');
  var activeModeNode = document.querySelector('.ic-mode-btn.active');
  var activeModeV = activeModeNode ? activeModeNode.getAttribute('data-mode') : 'predictive';
  var contextV = val('ic-context');
  var icuPhaseV = val('ic-icu-phase');

  /* Pregnancy requires female sex */
  if (sexV === 'M' && pregV !== 'none') {
    alert('Clinical warning: Pregnancy selected for a Male patient. Please review sex and pregnancy fields.');
    el('ic-pregnancy').classList.add('error');
    ok = false;
  } else if (el('ic-pregnancy')) {
    el('ic-pregnancy').classList.remove('error');
  }

  /* Pregnancy and lactation cannot both be active */
  if (pregV !== 'none' && lactV !== 'none') {
    alert('Clinical warning: Pregnancy and Lactation cannot both be active simultaneously. Please select only one.');
    el('ic-lactation').classList.add('error');
    ok = false;
  } else if (el('ic-lactation')) {
    el('ic-lactation').classList.remove('error');
  }

  /* ICU feeding phase changes the target and is therefore required in ICU. */
  if (contextV === 'icu' && !icuPhaseV) {
    alert('Select the ICU phase before calculating. The phase determines whether the displayed target is limited to acute-phase feeding, advanced during stabilisation, or shown at the measured/predicted target.');
    if (el('ic-icu-phase')) el('ic-icu-phase').classList.add('error');
    ok = false;
  } else if (el('ic-icu-phase')) {
    el('ic-icu-phase').classList.remove('error');
  }

  /* Never silently route a child from an adult selection to Schofield. */
  if (activeModeV === 'predictive' && ageV > 0 && ageV < 18 && regionV !== 'pediatric') {
    alert('Age is under 18 years. Select the Paediatric — Schofield W+H pathway before calculating so the displayed method matches the equation used.');
    if (el('ic-region')) el('ic-region').classList.add('error');
    ok = false;
  }
  if (ageV > 0 && ageV < 18 && (val('ic-protein-weight-basis') === 'ideal' || val('ic-protein-weight-basis') === 'adjusted')) {
    alert('Devine IBW and adult adjusted body weight are not used for children in this calculator. Choose Actual, clinician-verified Dry/Post-dialysis weight, or Auto, then apply an approved paediatric protocol.');
    if (el('ic-protein-weight-basis')) el('ic-protein-weight-basis').classList.add('error');
    ok = false;
  } else if (el('ic-protein-weight-basis')) {
    el('ic-protein-weight-basis').classList.remove('error');
  }

  /* Pediatric region should require age < 18 */
  if (activeModeV === 'predictive' && regionV === 'pediatric' && ageV >= 18) {
    alert('Paediatric Schofield is selected for an adult age. Select an adult equation; the calculator will not silently substitute another method.');
    if (el('ic-region')) el('ic-region').classList.add('error');
    ok = false;
  } else if (regionV !== 'body_comp' && el('ic-region')) {
    el('ic-region').classList.remove('error');
  }

  /* Body composition region REQUIRES LBM or body fat % - hard block */
  if (regionV === 'body_comp') {
    var lbmV = numVal('ic-lbm');
    var bfV  = numVal('ic-bf-pct');
    var regionNodeEl = el('ic-region');
    if (!lbmV && !bfV) {
      alert('Body Composition mode requires Lean Body Mass (kg) or Body Fat % entered in the Body Comp tab.\n\nWithout measured body composition data, Cunningham and Katch-McArdle equations cannot be reliably used. Please enter LBM or Body Fat %, or switch to a predictive equation (Mifflin-St Jeor, Henry Oxford, or ICMR-NIN 2020).');
      if (regionNodeEl) regionNodeEl.classList.add('error');
      ok = false;
    } else {
      if (regionNodeEl) regionNodeEl.classList.remove('error');
    }
  }

  /* LBM cannot exceed or equal body weight - physiologically impossible */
  var lbmInputV = numVal('ic-lbm');
  var weightInputV = numVal('ic-weight');
  var lbmNodeEl = el('ic-lbm');
  if (lbmInputV > 0 && weightInputV > 0 && lbmInputV >= weightInputV) {
    alert('LBM (' + lbmInputV + ' kg) must be less than body weight (' + weightInputV + ' kg). Lean body mass cannot equal or exceed total body weight - please check your entry.');
    if (lbmNodeEl) lbmNodeEl.classList.add('error');
    ok = false;
  } else {
    if (lbmNodeEl) lbmNodeEl.classList.remove('error');
  }

  /* RQ review range warning (non-blocking) */
  /* Already handled as a clinical flag inside doCalculate; no blocking alert needed */

  return ok;
}

/* ── 12. MAIN CALCULATE FUNCTION ────────────────────────── */
function calculate() {
  if (!validate()) return;

  var btn = el('ic-calculate-btn');
  if (btn) {
    btn.classList.add('loading');
    btn.innerHTML = '<svg class="spinner" width="18" height="18" fill="none" viewBox="0 0 18 18"><circle cx="9" cy="9" r="7" stroke="rgba(255,255,255,.3)" stroke-width="2"/><path d="M9 2a7 7 0 017 7" stroke="white" stroke-width="2" stroke-linecap="round"/></svg> Calculating…';
  }

  setTimeout(function() {
    doCalculate();
    if (btn) {
      btn.classList.remove('loading');
      btn.innerHTML = '<svg width="18" height="18" fill="none" viewBox="0 0 18 18"><path d="M9 3v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="9" cy="9" r="7" stroke="currentColor" stroke-width="1.8"/></svg> Calculate Metabolic Profile';
    }
  }, 300);
}

function doCalculate() {
  /* Collect inputs */
  var sex     = val('ic-sex');
  var A       = numVal('ic-age');
  var Wraw    = numVal('ic-weight');
  var Hcm     = numVal('ic-height');
  var region  = val('ic-region');
  var context = val('ic-context');
  var actKey  = context === 'icu' ? 'icu' : val('ic-activity');

  var vo2         = numVal('ic-vo2');
  var vco2_L      = numVal('ic-vco2-lmin');
  var vco2_mL     = numVal('ic-vco2-mlmin');
  var rq          = numVal('ic-rq') || 0.86;
  var UN          = numVal('ic-un');
  var fio2        = numVal('ic-fio2');         /* 0 = not entered */
  var fio2DeviceMax = numVal('ic-fio2-device-max');
  var steadyState = val('ic-steady-state') || 'unknown';
  var airLeak     = val('ic-air-leak') || 'none';
  /* New fields - item 6/7/8 */
  var interfaceType   = val('ic-interface-type') || 'none';
  var measDuration    = numVal('ic-meas-duration');
  var vo2Cv           = numVal('ic-vo2-cv');
  var vco2Cv          = numVal('ic-vco2-cv');
  var calibration     = val('ic-calibration') || 'full';
  /* VO₂-only fallback - item 9 */
  var vo2Only     = numVal('ic-vo2-only');
  var rqVo2Only   = numVal('ic-rq-vo2only') || 0.85;
  var activeModeBtn = document.querySelector('.ic-mode-btn.active');
  var activeMode = activeModeBtn ? activeModeBtn.getAttribute('data-mode') : 'full-ic';
  if (activeMode === 'predictive') {
    vo2 = 0; vco2_L = 0; vco2_mL = 0; vo2Only = 0;
  } else if (activeMode === 'vco2-only') {
    vo2 = 0; vco2_L = 0; vo2Only = 0;
  } else if (activeMode === 'vo2-only') {
    vo2 = 0; vco2_L = 0; vco2_mL = 0;
  } else {
    vco2_mL = 0; vo2Only = 0;
  }
  /* Protocol disturbances - item 13 */
  var disturbances = [];
  var distLabels = { exercise:'Physical activity', meal:'Meal within 2 hrs',
    agitation:'Agitation/distress', suction:'Airway suctioning',
    vent_change:'Ventilator change', position:'Position change',
    dialysis:'Dialysis/CRRT', fever_spike:'Fever spike' };
  document.querySelectorAll('#ic-protocol-checklist input:checked').forEach(function(cb) {
    disturbances.push(distLabels[cb.value] || cb.value);
  });
  var lbmDirect   = numVal('ic-lbm');
  var bfPct       = numVal('ic-bf-pct');
  var stressFactor= parseFloat(val('ic-stress-factor')) || 1.0;
  var icuPhase    = val('ic-icu-phase');
  var pregState   = val('ic-pregnancy') || 'none';
  var lactState   = val('ic-lactation') || 'none';
  var gerState    = val('ic-geriatric-state') || 'none';
  var oncoState   = val('ic-onco-condition') || 'none';
  var renalStatus = val('ic-renal-status') || 'none';
  var dryWeight   = numVal('ic-dry-weight');            /* [#4] */
  var proteinBasisSel = val('ic-protein-weight-basis') || 'auto'; /* [#4] */

  var Hm = Hcm / 100;
  var bmi = computeBMI(Wraw, Hcm);
  var IBW = computeIBW(Hcm, sex);
  var adultReferenceWeightApplicable = A >= 18;
  var flags = [];
  var REE = 0;
  var method = '';
  var tier = 3;
  var compRows = [];
  var quality = {
    type: 'low',
    label: 'Low confidence',
    score: 45,
    reason: 'Predictive equation result; review against clinical condition and consider indirect calorimetry when precision matters.'
  };
  var formulaExpression = '-';
  var formulaInputs = '-';
  var formulaAssumptions = '-';
  var targetPhaseLabel = 'No phase adjustment';

  /* ── Sync geriatric context → geriatric state (if not already set) ── */
  if (context === 'geriatric' && gerState === 'none') {
    gerState = 'active'; /* Default ESPEN geriatric baseline: 30 kcal/kg */
  }

  /* ── Sync oncology context → condition (if condition not already set) ── */
  if (oncoState === 'none') {
    if (context === 'oncology')            oncoState = 'standard';
    if (context === 'oncology_sarcopenia') oncoState = 'sarcopenia';
    if (context === 'oncology_cachexia')   oncoState = 'cachexia';
  }

  /* ── India PAL auto-remap: enforce ICMR-NIN 2020 PAL values ── */
  if (region === 'india') {
    if (actKey === 'sedentary_global') {
      actKey = 'sedentary_india';
    } else if (actKey === 'light_global' || actKey === 'moderate_global') {
      actKey = 'moderate_india';
    } else if (actKey === 'active_global' || actKey === 'very_active_global') {
      actKey = 'heavy_india';
    }
  }

  /* ── Body weight selection for obese (adults only; AdjBW/Devine-IBW is not
     validated in children — paediatric Schofield retains actual weight) ── */
  var W = Wraw;
  if (context === 'icu' && bmi >= 30 && W > IBW * 1.3 && A >= 18) {
    var AdjBW = computeAdjBW(Wraw, IBW);
    W = AdjBW;
    flags.push({ type: 'warn', msg: 'ICU obesity (BMI ' + bmi.toFixed(1) + '): Adjusted Body Weight ' + AdjBW.toFixed(1) + ' kg applied to predictive energy equations (Devine IBW ' + IBW.toFixed(1) + ' kg; AdjBW = IBW + 0.25 × excess) in line with the selected ICU approach.' });
  } else if (bmi >= 30 && A >= 18) {
    flags.push({ type: 'info', msg: 'Non-ICU obesity: actual weight ' + Wraw.toFixed(1) + ' kg remains the input to the selected predictive equation. Devine IBW and AdjBW are shown for audit but are not silently substituted.' });
  }

  /* ── LBM computation ── */
  /* Always use actual body weight (Wraw) for body composition derivation.
     Body fat % and Boer formula reflect the patient's actual fat-free mass
     regardless of energy-equation weight adjustments. AdjBW is only an
     energy-equation input and must not distort LBM/FFM values. */
  var LBM = lbmDirect > 0 ? lbmDirect : (bfPct > 0 ? Wraw * (1 - bfPct / 100) : estimateLBM(Wraw, Hcm, sex));
  var lbmSource = lbmDirect > 0 ? 'Direct (' + val('ic-bc-method') + ')' : (bfPct > 0 ? 'From body fat% (actual wt)' : 'Estimated - Boer formula (actual wt)');

  /* ────────────────────────────────────────────────────────
     TIER 1: FULL IC - WEIR
  ──────────────────────────────────────────────────────── */
  if (vo2 > 0 && vco2_L > 0) {
    tier = 1;
    if (UN > 0) {
      REE = EQ.weirComplete(vo2, vco2_L, UN);
      method = 'Complete Weir Equation (VO₂ + VCO₂ + Urinary Nitrogen)';
      formulaExpression = 'REE kcal/day = [(3.941 x VO2) + (1.106 x VCO2)] x 1440 - (2.17 x urinary nitrogen g/day)';
      formulaAssumptions = 'Urinary nitrogen supplied; no stress multiplier applied to measured IC.';
    } else {
      REE = EQ.weirAbbreviated(vo2, vco2_L);
      method = 'Abbreviated Weir Equation (VO₂ + VCO₂)';
      formulaExpression = 'REE kcal/day = [(3.941 x VO2) + (1.106 x VCO2)] x 1440';
      formulaAssumptions = 'Urinary nitrogen not provided; abbreviated Weir used. No stress multiplier applied to measured IC.';
    }
    var RQ_meas = (vco2_L / vo2);
    formulaInputs = 'VO2 ' + vo2.toFixed(3) + ' L/min; VCO2 ' + vco2_L.toFixed(3) + ' L/min; RQ ' + RQ_meas.toFixed(3) + (UN > 0 ? '; urinary nitrogen ' + UN.toFixed(2) + ' g/day' : '');
    var qualityCriticalReasons = [];
    var qualityCautionReasons = [];

    /* [#9] Unit-confusion guard: VO2/VCO2 above ~1.6 L/min at rest almost
       certainly means mL/min was entered (e.g. 250 instead of 0.250). */
    if (vo2 > 1.6 || vco2_L > 1.6) {
      flags.push({ type: 'critical', msg: 'VO₂/VCO₂ appear high for L/min (resting ICU range ~0.10–0.40 L/min). Did you enter mL/min (e.g. 250) instead of L/min (0.250)? Verify units before using this result.' });
      qualityCriticalReasons.push('VO2/VCO2 likely in mL/min, not L/min');
    }

    /* ── Calibration flag - item 8 ── */
    if (calibration === 'failed') {
      flags.push({ type: 'critical', msg: 'Calibration FAILED: IC results are invalid. Do not use these values clinically. Switch to VCO₂-only (Tier 2) or predictive equations until the metabolic cart is recalibrated.' });
      qualityCriticalReasons.push('calibration failed');
    } else if (calibration === 'not_performed') {
      flags.push({ type: 'warn', msg: 'Calibration not performed/documented. IC accuracy is unverified - interpret results with caution.' });
      qualityCautionReasons.push('calibration not documented');
    }

    /* ── Interface type flag - item 6 ── */
    if (interfaceType === 'ecmo') {
      flags.push({ type: 'critical', msg: 'ECMO circuit interface: oxygen and CO₂ exchange from the ECMO membrane circuit confounds gas exchange measurement. True pulmonary VO₂/VCO₂ cannot be separated from ECMO gas exchange. IC values unreliable.' });
      qualityCriticalReasons.push('ECMO circuit confounds gas exchange');
    } else if (interfaceType === 'niv') {
      flags.push({ type: 'warn', msg: 'NIV mask interface: air leaks around the mask are common during NIV, causing VO₂ underestimation. Verify tight mask seal before accepting values.' });
      qualityCautionReasons.push('NIV mask leak risk');
    }

    /* ── Duration-dependent steady-state criterion ──
       Accepted evidence-based routes: 5–24 min with both CV values ≤5%, or
       ≥25 min with both CV values ≤10%. Missing evidence is caution, not an
       undocumented pass. */
    var steadyCriterion = measDuration >= 25 ? 10 : (measDuration >= 5 ? 5 : null);
    if (!measDuration) {
      flags.push({ type: 'warn', msg: 'Measurement duration was not documented. Record the analysed interval before treating the IC result as high confidence.' });
      qualityCautionReasons.push('measurement duration not documented');
    } else if (measDuration < 5) {
      flags.push({ type: 'critical', msg: 'Measurement duration ' + measDuration + ' min is below the minimum 5-minute stable interval. Repeat or extend the measurement.' });
      qualityCriticalReasons.push('measurement duration below 5 minutes');
    }
    if (val('ic-vo2-cv') === '') {
      flags.push({ type: 'warn', msg: 'VO₂ CV was not documented. Enter CV% for the analysed interval to support the steady-state claim.' });
      qualityCautionReasons.push('VO2 CV not documented');
    } else if (steadyCriterion != null && vo2Cv > steadyCriterion) {
      flags.push({ type: 'critical', msg: 'VO₂ CV ' + vo2Cv.toFixed(1) + '% exceeds the ' + steadyCriterion + '% limit for a ' + measDuration + '-minute analysed interval.' });
      qualityCriticalReasons.push('VO2 CV exceeds duration criterion');
    }
    if (val('ic-vco2-cv') === '') {
      flags.push({ type: 'warn', msg: 'VCO₂ CV was not documented. Enter CV% for the analysed interval to support the steady-state claim.' });
      qualityCautionReasons.push('VCO2 CV not documented');
    } else if (steadyCriterion != null && vco2Cv > steadyCriterion) {
      flags.push({ type: 'critical', msg: 'VCO₂ CV ' + vco2Cv.toFixed(1) + '% exceeds the ' + steadyCriterion + '% limit for a ' + measDuration + '-minute analysed interval.' });
      qualityCriticalReasons.push('VCO2 CV exceeds duration criterion');
    }

    flags.push({ type: 'success', msg: 'Tier 1 - Full IC active. Measured RQ: ' + RQ_meas.toFixed(3) + '. All predictive equations overridden when measurement quality is acceptable.' });

    /* ── Two-level RQ warning - item 12 ── */
    if (RQ_meas < 0.67 || RQ_meas > 1.30) {
      flags.push({ type: 'critical', msg: 'RQ ' + RQ_meas.toFixed(3) + ' is outside the critical validity range (<0.67 or >1.30). Repeat IC or use an alternative method after checking calibration, circuit integrity, acid-base status, and ventilator conditions.' });
      qualityCriticalReasons.push('RQ outside critical validity range');
    } else if (RQ_meas < 0.70 || RQ_meas > 1.00) {
      flags.push({ type: 'warn', msg: 'RQ ' + RQ_meas.toFixed(3) + ' is outside expected resting range (0.70–1.00). Review substrate mix, overfeeding/lipogenesis, ketosis/starvation, acid-base status, and measurement conditions before accepting the result.' });
      qualityCautionReasons.push('RQ outside expected resting range');
    }

    /* ── IC measurement quality flags ── */
    if (fio2 > 0 && fio2DeviceMax > 0 && fio2 > fio2DeviceMax) {
      flags.push({ type: 'critical', msg: 'FiO₂ ' + fio2.toFixed(2) + ' exceeds the entered device-validated limit of ' + fio2DeviceMax.toFixed(2) + '. Withhold this IC target and repeat within the device specification or use an independent alternative pathway.' });
      qualityCriticalReasons.push('FiO2 exceeds device-validated limit');
    } else if (fio2 > 0 && fio2DeviceMax > 0 && fio2 >= fio2DeviceMax - 0.05) {
      flags.push({ type: 'warn', msg: 'FiO₂ ' + fio2.toFixed(2) + ' is close to the entered device-validated limit of ' + fio2DeviceMax.toFixed(2) + '. Confirm the device instructions, sampling method, and local validation.' });
      qualityCautionReasons.push('FiO2 close to device limit');
    } else if (fio2 > 0 && !(fio2DeviceMax > 0)) {
      flags.push({ type: 'warn', msg: 'FiO₂ ' + fio2.toFixed(2) + ' was entered without a device-validated upper limit. No universal FiO₂ cut-off has been assumed; verify the metabolic cart instructions for use before accepting the result.' });
      qualityCautionReasons.push('device FiO2 limit not documented');
    }
    if (steadyState === 'no') {
      flags.push({ type: 'critical', msg: 'Steady state was recorded as not achieved. Current gas values may reflect a transient state; repeat after stabilisation.' });
      qualityCriticalReasons.push('steady-state not achieved');
    } else if (steadyState === 'unknown') {
      flags.push({ type: 'warn', msg: 'Steady-state status was not assessed. Confirm either a 5–24 minute interval with VO₂/VCO₂ CV ≤5%, or an interval of at least 25 minutes with both CV values ≤10%.' });
      qualityCautionReasons.push('steady-state not assessed');
    }
    if (airLeak === 'confirmed') {
      flags.push({ type: 'critical', msg: 'Confirmed air leak present. VO₂ and VCO₂ readings are unreliable - inspired vs expired gas volumes do not balance. Do not use these IC values. Switch to VCO₂-only or predictive method.' });
      qualityCriticalReasons.push('confirmed air leak');
    } else if (airLeak === 'suspected') {
      flags.push({ type: 'warn', msg: 'Suspected air leak (tube/cuff/mask). Verify circuit integrity before relying on these values. Even small leaks cause systematic VO₂ underestimation.' });
      qualityCautionReasons.push('suspected air leak');
    }

    /* ── Protocol disturbances - item 13 ── */
    if (disturbances.length > 0) {
      flags.push({ type: 'warn', msg: 'Protocol disturbances noted: ' + disturbances.join(', ') + '. These may have elevated or depressed measured metabolic rate. Consider repeat measurement after ≥30 min of rest without disturbances.' });
      qualityCautionReasons.push('protocol disturbances');
    }

    if (qualityCriticalReasons.length > 0) {
      quality = {
        type: 'invalid',
        label: 'Invalid',
        score: 0,
        reason: 'Repeat IC or use an alternative method before clinical interpretation: ' + qualityCriticalReasons.join(', ') + '.'
      };
    } else if (qualityCautionReasons.length > 0) {
      quality = {
        type: 'caution',
        label: 'Moderate confidence',
        score: 65,
        reason: 'IC result has review flags: ' + qualityCautionReasons.join(', ') + '.'
      };
    } else {
      quality = {
        type: 'valid',
        label: 'High confidence',
        score: 90,
        reason: 'Full IC result meets entered quality checks. Confirm protocol documentation before clinical use.'
      };
    }

  /* ────────────────────────────────────────────────────────
     TIER 2: VCO₂-ONLY (VENTILATOR)
  ──────────────────────────────────────────────────────── */
  } else if (vco2_mL > 0) {
    tier = 2;
    var vcoCriticalReasons = [];
    var vcoCautionReasons = [];
    var vcoMult = EQ.vcoMultiplier(rq);
    REE = vco2_mL * vcoMult;
    method = 'VCO₂-Only (Ventilator-Derived) · RQ ' + rq.toFixed(2) + ' · ×' + vcoMult.toFixed(2);
    formulaInputs = 'VCO2 ' + Math.round(vco2_mL) + ' mL/min; assumed RQ ' + rq.toFixed(2) + '; derived multiplier ' + vcoMult.toFixed(3);
    formulaExpression = 'REE kcal/day = VCO2(mL/min) × ' + vcoMult.toFixed(2) + '   [multiplier = ((3.941 / RQ) + 1.106) × 1.44; ×8.19 assumes RQ ≈ 0.86]';
    formulaAssumptions = 'Multiplier recomputed from the selected RQ (not a fixed 8.19). Assumed RQ used because measured RQ requires both VO2 and VCO2. No stress multiplier applied to VCO2-derived REE.';
    quality = {
      type: 'low',
      label: 'Moderate confidence',
      score: 70,
      reason: 'Ventilator-derived VCO2 (RQ ' + rq.toFixed(2) + ', ×' + vcoMult.toFixed(2) + ') is preferred over predictive equations when full IC is unavailable, but it remains lower confidence than full IC.'
    };
    flags.push({ type: 'info', msg: 'Tier 2 — VCO₂-only method. Assumed RQ ' + rq.toFixed(2) + ' → multiplier ×' + vcoMult.toFixed(2) + '. The commonly cited ×8.19 assumes RQ ≈ 0.86; at RQ 0.85 it is ≈8.27. Preferred over anthropometric equations for mechanically ventilated patients when full IC is unavailable.' });
    /* [#9] Unit-confusion guard: a value below ~20 in the mL/min field is almost
       certainly L/min entered by mistake (a 1000-fold error). */
    if (vco2_mL < 20) {
      flags.push({ type: 'critical', msg: 'VCO₂ ' + vco2_mL + ' mL/min is implausibly low for ventilator VCO₂ (typical ICU adult 100–300 mL/min). Did you enter L/min (e.g. 0.20) instead of mL/min (200)? Verify units before using this result.' });
      quality = { type: 'invalid', label: 'Check units', score: 0, reason: 'VCO₂ appears to be entered in L/min, not mL/min - a 1000-fold unit error.' };
    }
    /* Two-level RQ warning for assumed RQ - item 12 */
    if (rq < 0.67 || rq > 1.30) {
      flags.push({ type: 'critical', msg: 'Assumed RQ ' + rq.toFixed(2) + ' is outside the critical validity range (<0.67 or >1.30). VCO₂-only estimate will be substantially unreliable; use full IC or a clinically defensible assumed RQ.' });
      quality = { type: 'invalid', label: 'Invalid', score: 0, reason: 'Assumed RQ is outside the critical validity range.' };
    } else if (rq < 0.70 || rq > 1.00) {
      flags.push({ type: 'warn', msg: 'Assumed RQ ' + rq.toFixed(2) + ' is outside expected resting range (0.70–1.00). Review clinical substrate status before accepting the estimate.' });
      quality = { type: 'caution', label: 'Low confidence', score: 55, reason: 'VCO2-only estimate depends on an assumed RQ outside the expected resting range.' };
    }
    var vcoSteadyCriterion = measDuration >= 25 ? 10 : (measDuration >= 5 ? 5 : null);
    if (!measDuration) {
      flags.push({ type: 'warn', msg: 'VCO₂ measurement duration was not documented. Record the analysed interval and stability criteria.' });
      vcoCautionReasons.push('duration not documented');
    } else if (measDuration < 5) {
      flags.push({ type: 'critical', msg: 'VCO₂ measurement duration ' + measDuration + ' min is below the minimum 5-minute stable interval.' });
      vcoCriticalReasons.push('duration below 5 minutes');
    }
    if (val('ic-vco2-cv') === '') {
      flags.push({ type: 'warn', msg: 'VCO₂ CV was not documented. The ventilator-derived estimate cannot be treated as a stable measurement without this evidence.' });
      vcoCautionReasons.push('VCO2 CV not documented');
    } else if (vcoSteadyCriterion != null && vco2Cv > vcoSteadyCriterion) {
      flags.push({ type: 'critical', msg: 'VCO₂ CV ' + vco2Cv.toFixed(1) + '% exceeds the ' + vcoSteadyCriterion + '% limit for a ' + measDuration + '-minute interval.' });
      vcoCriticalReasons.push('VCO2 CV exceeds duration criterion');
    }
    if (steadyState === 'no') {
      flags.push({ type: 'critical', msg: 'Steady state was not achieved for the ventilator-derived VCO₂ measurement.' });
      vcoCriticalReasons.push('steady state not achieved');
    } else if (steadyState === 'unknown') {
      flags.push({ type: 'warn', msg: 'Steady-state status was not assessed for the ventilator-derived VCO₂ measurement.' });
      vcoCautionReasons.push('steady state not assessed');
    }
    if (calibration === 'failed') {
      flags.push({ type: 'critical', msg: 'The VCO₂ source device calibration or verification failed. Do not use the gas-derived estimate.' });
      vcoCriticalReasons.push('device verification failed');
    } else if (calibration === 'not_performed') {
      flags.push({ type: 'warn', msg: 'The VCO₂ source device calibration or verification was not documented.' });
      vcoCautionReasons.push('device verification not documented');
    }
    if (airLeak === 'confirmed') {
      flags.push({ type: 'critical', msg: 'Confirmed circuit leak: the ventilator-derived VCO₂ is not a valid fallback because the same leaking circuit produced the value.' });
      vcoCriticalReasons.push('confirmed circuit leak');
    } else if (airLeak === 'suspected') {
      flags.push({ type: 'warn', msg: 'Suspected circuit leak may bias ventilator-derived VCO₂. Verify the circuit before accepting the estimate.' });
      vcoCautionReasons.push('suspected circuit leak');
    }
    if (interfaceType === 'ecmo') {
      flags.push({ type: 'critical', msg: 'ECMO gas exchange is not captured by ventilator VCO₂ alone. This value does not represent whole-body CO₂ production.' });
      vcoCriticalReasons.push('ECMO gas exchange not captured');
    } else if (interfaceType !== 'ventilator') {
      flags.push({ type: 'warn', msg: 'VCO₂-only mode is intended for a validated ventilator-derived VCO₂ value. Confirm the source device and interface.' });
      vcoCautionReasons.push('ventilator source not confirmed');
    }
    if (disturbances.length > 0) {
      flags.push({ type: 'warn', msg: 'Protocol disturbances noted during the VCO₂ interval: ' + disturbances.join(', ') + '.' });
      vcoCautionReasons.push('protocol disturbances');
    }
    if (vcoCriticalReasons.length) {
      quality = { type: 'invalid', label: 'Invalid', score: 0, reason: 'Do not use this VCO2-derived estimate: ' + vcoCriticalReasons.join(', ') + '.' };
    } else if (vcoCautionReasons.length && quality.type !== 'invalid') {
      quality = { type: 'caution', label: 'Low confidence', score: 55, reason: 'VCO2-derived estimate requires review: ' + vcoCautionReasons.join(', ') + '.' };
    }

  /* ────────────────────────────────────────────────────────
     TIER 2b: VO₂-ONLY FALLBACK - item 9
  ──────────────────────────────────────────────────────── */
  } else if (vo2Only > 0) {
    tier = 2;
    /* Estimate VCO₂ from assumed RQ: VCO₂ = VO₂ × RQ, then use abbreviated Weir */
    var vco2Est = vo2Only * rqVo2Only;
    REE = EQ.weirAbbreviated(vo2Only, vco2Est);
    method = 'VO₂-Only Fallback - Estimated VCO₂ (RQ ' + rqVo2Only.toFixed(2) + ')';
    formulaExpression = 'Estimated VCO2 = VO2 x assumed RQ; REE kcal/day = [(3.941 x VO2) + (1.106 x estimated VCO2)] x 1440';
    formulaInputs = 'VO2 ' + vo2Only.toFixed(3) + ' L/min; assumed RQ ' + rqVo2Only.toFixed(2) + '; estimated VCO2 ' + vco2Est.toFixed(3) + ' L/min';
    formulaAssumptions = 'Lower confidence fallback. Obtain measured VCO2 when possible for full IC.';
    quality = {
      type: 'low',
      label: 'Low confidence',
      score: 45,
      reason: 'VO2-only estimate depends on assumed RQ and estimated VCO2. Prefer full IC or VCO2-only ventilator mode when available.'
    };
    flags.push({ type: 'warn', msg: 'VO₂-only fallback mode: VCO₂ estimated as VO₂ × assumed RQ (' + rqVo2Only.toFixed(2) + ') = ' + vco2Est.toFixed(3) + ' L/min. Accuracy depends on RQ assumption. Obtain VCO₂ measurement when possible for full IC (Tier 1).' });
    if (rqVo2Only < 0.67 || rqVo2Only > 1.30) {
      flags.push({ type: 'critical', msg: 'Assumed RQ ' + rqVo2Only.toFixed(2) + ' is outside the critical validity range (<0.67 or >1.30). VO₂-only estimate is not clinically interpretable.' });
      quality = { type: 'invalid', label: 'Invalid', score: 0, reason: 'Assumed RQ is outside the critical validity range.' };
    } else if (rqVo2Only < 0.70 || rqVo2Only > 1.00) {
      flags.push({ type: 'warn', msg: 'Assumed RQ ' + rqVo2Only.toFixed(2) + ' is outside expected resting range (0.70–1.00). Review clinical context before accepting this fallback estimate.' });
      quality = { type: 'caution', label: 'Low confidence', score: 55, reason: 'VO2-only estimate uses an assumed RQ outside the expected resting range.' };
    }
    if (disturbances.length > 0) {
      flags.push({ type: 'warn', msg: 'Protocol disturbances noted: ' + disturbances.join(', ') + '.' });
    }

  /* ────────────────────────────────────────────────────────
     TIER 3A: BODY COMPOSITION EQUATIONS
  ──────────────────────────────────────────────────────── */
  } else if (region === 'body_comp') {
    tier = 3;
    var ree_c = EQ.cunningham(LBM);
    var ree_k = EQ.katchMcArdle(LBM);
    REE = (ree_c + ree_k) / 2;
    method = 'Body Composition Mean (Cunningham 1980 + Katch-McArdle 1996)';
    formulaExpression = 'REE = [(500 + 22 x LBM) + (370 + 21.6 x LBM)] / 2';
    formulaInputs = 'LBM ' + LBM.toFixed(1) + ' kg; source: ' + lbmSource;
    formulaAssumptions = 'Body composition estimate used. Measurement method and hydration status affect accuracy.';
    flags.push({ type: 'success', msg: 'LBM-based equations active (' + lbmSource + '). LBM: ' + LBM.toFixed(1) + ' kg. Cunningham: ' + Math.round(ree_c) + ' kcal · Katch-McArdle: ' + Math.round(ree_k) + ' kcal - mean used.' });

  /* ────────────────────────────────────────────────────────
     TIER 3B: PEDIATRIC SCHOFIELD
  ──────────────────────────────────────────────────────── */
  } else if (region === 'pediatric' || A < 18) {
    tier = 3;
    var ree_s = EQ.schofieldPediatric(W, Hm, A, sex);
    if (ree_s !== null) {
      REE = ree_s;
      method = 'Schofield W+H Pediatric (FAO/WHO/UNU 1985)';
      formulaExpression = 'Schofield pediatric W+H age/sex equation';
      formulaInputs = 'Weight basis ' + W.toFixed(1) + ' kg; height ' + Hm.toFixed(2) + ' m; age ' + A.toFixed(1) + ' years; sex ' + sex;
      formulaAssumptions = 'Paediatric predictive equation; use age-appropriate clinical interpretation.';
      flags.push({ type: 'info', msg: 'Paediatric mode. ICMR-NIN 2020 does NOT reduce pediatric BMR (insufficient evidence) - full Schofield retained.' });
      flags.push({ type: 'critical', msg: 'ASPEN/SCCM: IC strongly recommended for critically ill paediatric patients. Schofield accuracy: only ~48% within 10% of measured EE in PICU cohorts.' });
    } else {
      REE = EQ.mifflinStJeor(W, Hcm, A, sex);
      method = 'Mifflin-St Jeor (Schofield returned null for age range)';
      formulaExpression = 'REE = (10 x weight) + (6.25 x height) - (5 x age) + sex constant';
      formulaInputs = 'Weight basis ' + W.toFixed(1) + ' kg; height ' + Hcm.toFixed(1) + ' cm; age ' + A.toFixed(1) + ' years; sex ' + sex;
      formulaAssumptions = 'Adult fallback because Schofield pediatric did not apply.';
    }

  /* ────────────────────────────────────────────────────────
     TIER 3C: INDIAN ADULT - ICMR-NIN 2020
  ──────────────────────────────────────────────────────── */
  } else if (region === 'india' && A >= 18) {
    tier = 3;
    REE = EQ.icmrNIN2020(W, A, sex);
    method = 'ICMR-NIN 2020 Adjusted (Indian Phenotype)';
    var corrPct = sex === 'M' ? '10%' : '9%';
    formulaExpression = 'ICMR-NIN 2020 adult adjustment = Schofield W-only BMR x ' + (sex === 'M' ? '0.90' : '0.91');
    formulaInputs = 'Weight basis ' + W.toFixed(1) + ' kg; age ' + A.toFixed(1) + ' years; sex ' + sex;
    formulaAssumptions = 'Indian adult adjustment: lower adult BMR estimates are used because international equations may overestimate BMR in Indian populations.';
    flags.push({ type: 'success', msg: 'Indian adult adjustment: ICMR-NIN 2020 applies lower adult BMR estimates for Indian populations because international equations may overestimate BMR in Indians. Applied correction: −' + corrPct + ' from Schofield weight-only base.' });

  /* ────────────────────────────────────────────────────────
     TIER 3D: HENRY OXFORD GLOBAL
  ──────────────────────────────────────────────────────── */
  } else if (region === 'global_oxford' && A >= 18) {
    tier = 3;
    REE = EQ.henryOxford(W, Hcm, A, sex);
    method = 'Henry Oxford Equation (2005)';
    formulaExpression = 'Henry Oxford 2005 age/sex coefficient equation';
    formulaInputs = 'Weight basis ' + W.toFixed(1) + ' kg; height ' + Hcm.toFixed(1) + ' cm; age ' + A.toFixed(1) + ' years; sex ' + sex;
    formulaAssumptions = 'Predictive equation for diverse adult cohorts; measured IC remains preferred when available.';
    flags.push({ type: 'info', msg: 'Henry Oxford: corrected for Italian cohort bias in Schofield dataset. Validated on tropical/developing country populations. Recommended for diverse global adult cohorts.' });
    if (A > 80) {
      flags.push({ type: 'warn', msg: 'Henry Oxford (2005) was validated primarily for adults up to ~80 years. Limited evidence exists for octogenarians — treat result with clinical caution and consider IC measurement.' });
    }

  /* ────────────────────────────────────────────────────────
     TIER 3 FALLBACK: MIFFLIN-ST JEOR
  ──────────────────────────────────────────────────────── */
  } else {
    tier = 3;
    REE = EQ.mifflinStJeor(W, Hcm, A, sex);
    method = 'Mifflin-St Jeor (1990)';
    formulaExpression = 'REE = (10 x weight) + (6.25 x height) - (5 x age) + sex constant';
    formulaInputs = 'Weight basis ' + W.toFixed(1) + ' kg; height ' + Hcm.toFixed(1) + ' cm; age ' + A.toFixed(1) + ' years; sex ' + sex;
    formulaAssumptions = 'General adult predictive equation. Consider IC where clinical precision is required.';
    if (A < 19 || A > 78) {
      flags.push({ type: 'warn', msg: 'Mifflin-St Jeor was derived in adults aged 19–78 years (Mifflin 1990). Age ' + A.toFixed(0) + ' is outside that derivation range; consider a more applicable method or indirect calorimetry.' });
    }
  }

  /* ── Apply stress factor (predictive / Tier 3 only) ── */
  /* Never apply to measured IC (Tier 1) or VCO₂-only (Tier 2):
     those are already the patient's actual measured metabolic rate. */
  if (tier === 3 && stressFactor > 1.0) {
    REE = REE * stressFactor;
    method += ' × stress factor ' + stressFactor;
    formulaAssumptions += ' Stress multiplier ' + stressFactor + ' applied because this is a predictive estimate.';
    flags.push({ type: 'warn', msg: 'Stress factor ' + stressFactor + '× applied to predicted REE. Use with caution - IC measurement always preferred over stress multipliers in critical illness.' });
  }
  if (tier < 3 && stressFactor > 1.0) {
    flags.push({ type: 'info', msg: 'Stress factor ignored: measured IC/VCO₂ data already reflects the patient\'s actual metabolic rate. Stress multipliers apply only to predictive equations.' });
  }

  /* ── PAL / TDEE ── */
  var pediatricTargetWithheld = A < 18;
  var palVal = pediatricTargetWithheld ? 1.00 : (PAL_TABLE[actKey] || 1.40);
  var palLabel = pediatricTargetWithheld ? 'Not applied — paediatric prescription protocol required' : (PAL_LABELS[actKey] || actKey);
  var TDEE = REE * palVal;
  if (pediatricTargetWithheld) {
    flags.push({ type: 'critical', msg: 'Paediatric safety gate: REE is shown, but the final energy target is withheld because this calculator does not implement age-specific paediatric activity, growth, critical-care, and disease-phase prescription protocols. Review with a paediatric dietitian and an approved local protocol.' });
  }

  /* ── finalTarget default (may be overridden below) ── */
  var finalTarget = TDEE;
  var predictiveWeekOne = context === 'icu' && tier === 3 && (icuPhase === 'acute' || icuPhase === 'stable');
  var energyOperator = predictiveWeekOne ? '<' : '=';

  /* ── ICU flags and targets ── */
  if (context === 'icu') {
    if (tier === 3) {
      flags.push({ type: 'critical', msg: 'ICU context: indirect calorimetry is preferred. Predictive equations may be substantially inaccurate in critical illness; escalate to full IC or VCO₂-derived estimation when feasible.' });
    } else {
      flags.push({ type: 'info', msg: 'ICU context: measured or gas-derived EE selected. Do not add stress multipliers to measured IC/VCO₂-derived REE.' });
    }
    if (!predictiveWeekOne && icuPhase === 'acute') {
      finalTarget = TDEE * 0.70;
      targetPhaseLabel = 'ICU acute phase, day 0–3: 70% of measured/predicted target applied.';
      flags.push({ type: 'warn', msg: 'Acute phase (Day 0–3): avoid early full feeding; hypocaloric target applied at 70% of measured/predicted target (' + Math.round(finalTarget) + ' kcal/day). Reassess frequently.' });
      flags.push({ type: 'warn', msg: 'Acute ICU protein: advance progressively toward the selected protocol target rather than assuming the full dose is appropriate on day one. Document the local progression plan and reassess tolerance, renal function, and catabolic losses.' });
    }
    if (!predictiveWeekOne && icuPhase === 'stable') {
      finalTarget = TDEE * 0.90;
      targetPhaseLabel = 'ICU stabilisation phase, day 3–7: gradual progression toward 80–100%; 90% target displayed.';
      flags.push({ type: 'info', msg: 'Stabilisation phase (Day 3–7): progress gradually toward 80–100% of measured EE. Displayed target = 90%; adjust to tolerance and refeeding risk. Under the ESPEN protocol, progress protein toward 1.3 g/kg/day.' });
    }
    if (icuPhase === 'recovery') {
      targetPhaseLabel = 'ICU recovery phase: measured/predicted target displayed; reassess frequently.';
      flags.push({ type: 'info', msg: 'Recovery phase: aim for measured target when clinically appropriate and reassess with condition changes.' });
    }
    if (val('ic-feeding-route') === 'enteral') {
      flags.push({ type: 'info', msg: 'Enteral route: if <60% of energy/protein target met by Day 3–7, consider supplemental PN (SPN) per local ICU nutrition protocol and ASPEN/SCCM or ESPEN ICU guidance.' });
    }
  }
  if (context === 'inpatient') {
    flags.push({ type: 'info', msg: 'Inpatient ward setting selected. The chosen activity factor is shown explicitly; ICU phase progression is not applied.' });
  }
  /* ── Geriatric ESPEN overlay ── */
  var espenMin = null, espenMax = null;
  if (A >= 65 || gerState !== 'none') {
    var espenTarget = '';
    if (gerState === 'underweight' || bmi < 21) {
      espenMin = 32 * W; espenMax = 38 * W;
      espenTarget = '32–38 kcal/kg/day (ESPEN underweight, BMI <21)';
    } else if (gerState === 'bedbound' || context === 'icu') {
      espenMin = 27 * W; espenMax = 30 * W;
      espenTarget = '27–30 kcal/kg/day (ESPEN bedbound/ICU)';
    } else {
      espenMin = 30 * W; espenMax = 35 * W;
      espenTarget = '30–35 kcal/kg/day (ESPEN geriatric baseline, ambulatory/active)';
    }
    /* Only override finalTarget when:
       (a) ICU acute phase has NOT already set it, AND
       (b) we are NOT using a measured IC/VCO₂ value (Tier 1 or 2).
       Measured REE already reflects the patient's actual metabolic rate - a
       kcal/kg population target should never override it. */
    if (!predictiveWeekOne && !(context === 'icu' && icuPhase === 'acute') && tier === 3) {
      finalTarget = espenMin;
    }
    flags.push({ type: 'info', msg: 'Geriatric (≥65 yrs): ESPEN 2022 energy target ' + espenTarget + ' → ' + Math.round(espenMin) + (espenMax !== espenMin ? '–' + Math.round(espenMax) : '') + ' kcal/day. Protein: 1.0–1.2 g/kg/day (healthy); increase to 1.2–1.5 g/kg/day with acute illness, sarcopenia, or malnutrition (ESPEN Geriatric 2022).' });
  }

  /* ── Pregnancy / Lactation additions ── */
  var addList = [];
  var pregRegionKey = region === 'india' ? 'india' : 'global';
  var pregAdd = PREG_KCAL[pregState] ? PREG_KCAL[pregState][pregRegionKey] : 0;
  var lactAdd = LACT_KCAL[lactState] ? LACT_KCAL[lactState][pregRegionKey] : 0;

  if (pregAdd > 0) {
    addList.push({ label: 'Pregnancy (' + pregState.toUpperCase() + ')', val: pregAdd, src: 'ICMR-NIN 2020' });
    formulaAssumptions += ' Pregnancy addition +' + pregAdd + ' kcal/day applied.';
    flags.push({ type: 'info', msg: 'Pregnancy (' + pregState + '): +' + pregAdd + ' kcal/day added (' + (region === 'india' ? 'ICMR-NIN 2020' : 'Global guideline') + ').' });
  }
  if (lactAdd > 0) {
    addList.push({ label: 'Lactation (' + (lactState === 'early' ? '0–6 mo' : '7–12 mo') + ')', val: lactAdd, src: 'ICMR-NIN 2020' });
    formulaAssumptions += ' Lactation addition +' + lactAdd + ' kcal/day applied.';
    flags.push({ type: 'info', msg: 'Lactation (' + (lactState === 'early' ? '0–6 months' : '7–12 months') + '): +' + lactAdd + ' kcal/day added (' + (region === 'india' ? 'ICMR-NIN 2020' : 'Global guideline') + ').' });
  }

  /* ── Oncology targets - applied to finalTarget ── */
  var oncoMin = null, oncoMax = null;
  if (oncoState !== 'none') {
    if (oncoState === 'standard')        { oncoMin = 25 * W; oncoMax = 30 * W; }
    else if (oncoState === 'sarcopenia') { oncoMin = 30 * W; oncoMax = 35 * W; }
    else if (oncoState === 'cachexia')   { oncoMin = 30 * W; oncoMax = 35 * W; }
    /* Oncology kcal/kg overrides finalTarget for predictive tier 3 only (and not
       when ICU acute already set it). Measured IC/VCO2 (tier 1/2) is never
       overridden by a population kcal/kg target — range shown as reference. */
    var oncoOverrode = false;
    if (!predictiveWeekOne && !(context === 'icu' && icuPhase === 'acute') && tier === 3) {
      finalTarget = oncoMin;
      targetPhaseLabel = 'Oncology context: kcal/kg range displayed; final target set to range minimum.';
      oncoOverrode = true;
    }
    addList.push({
      label: 'Oncology Target Range',
      range: Math.round(oncoMin) + '–' + Math.round(oncoMax) + ' kcal/day',
      src: 'IAPEN India'
    });
    flags.push({ type: 'warn', msg: 'Oncology target (ESPEN Cancer 2021 / IAPEN India): ' + Math.round(oncoMin) + '–' + Math.round(oncoMax) + ' kcal/day (' + (oncoState === 'standard' ? '25–30' : '30–35') + ' kcal/kg/day). ' + (oncoOverrode ? 'Final target set to range minimum.' : 'Shown as reference; measured/gas-derived REE retained as target.') + ' Protein: ' + (oncoState === 'standard' ? '1.0–1.5 g/kg/day' : '1.2–1.5 g/kg/day with individual review') + '.' });
  }

  finalTarget = finalTarget + pregAdd + lactAdd;
  if (predictiveWeekOne) {
    finalTarget = TDEE * 0.70;
    targetPhaseLabel = 'ESPEN 2023: below 70% of predicted needs during the first ICU week. This is an exclusive ceiling, not a fixed delivery prescription; whole-kcal display is rounded down.';
    flags.push({type:'warn',msg:targetPhaseLabel});
    formulaAssumptions += ' Predictive first-week ICU ceiling takes precedence over kcal/kg overlays and outpatient increments. Macro amounts at this ceiling are arithmetic references, not prescribed feeding doses.';
  }
  var energyDisplay = (predictiveWeekOne ? '<' : '') + (predictiveWeekOne ? Math.floor(finalTarget) : Math.round(finalTarget)).toLocaleString('en-IN');

  /* ── Comparison table - now always generated (items 4 & 11) ── */
  /* Show for all tiers so measured values can be compared against predictions */
  if (A >= 18) {
    var compEquations = [
      { name: 'Mifflin-St Jeor (1990)', ree: EQ.mifflinStJeor(W, Hcm, A, sex), note: 'Recommended (modern)', tag: region === 'global_mifflin' ? 'selected' : '' },
      { name: 'Henry Oxford (2005)', ree: EQ.henryOxford(W, Hcm, A, sex), note: 'Recommended (diverse)', tag: region === 'global_oxford' ? 'selected' : '' },
      { name: 'Harris-Benedict (1984)', ree: EQ.harrisBenedict(W, Hcm, A, sex), note: '⚠ Legacy', tag: 'legacy' },
      { name: 'ICMR-NIN 2020', ree: EQ.icmrNIN2020(W, A, sex), note: 'India phenotype', tag: region === 'india' ? 'selected' : 'india' },
      { name: 'Schofield W-only', ree: EQ.schofieldWeightOnly(W, A, sex), note: 'W-only base (no height)', tag: '' }
    ];
    if (lbmDirect > 0 || bfPct > 0) {
      compEquations.push({ name: 'Cunningham (1980)', ree: EQ.cunningham(LBM), note: 'LBM-based', tag: '' });
      compEquations.push({ name: 'Katch-McArdle (1996)', ree: EQ.katchMcArdle(LBM), note: 'LBM-based', tag: '' });
    }
    var reeValues = compEquations.map(function(e) { return e.ree; });
    var mean = reeValues.reduce(function(a, b) { return a + b; }, 0) / reeValues.length;
    compRows = compEquations.map(function(e) {
      var delta = ((e.ree - mean) / mean * 100);
      return {
        name: e.name,
        ree: Math.round(e.ree),
        delta: (delta >= 0 ? '+' : '') + delta.toFixed(1) + '% vs mean',
        isSelected: (e.tag === 'selected'),
        tag: e.tag
      };
    });
    /* High variance flag */
    var spread = Math.max.apply(null, reeValues) - Math.min.apply(null, reeValues);
    if (spread > 200) {
      flags.push({ type: 'warn', msg: 'High inter-equation variance (' + Math.round(spread) + ' kcal/day spread). Equation selection significantly impacts outcome. Consider IC measurement for clinical precision.' });
    }
  }

  /* ── Protein recommendation ── */
  /* Protein targets always use ACTUAL body weight (Wraw), not AdjBW.
     AdjBW is only an energy-equation input for obese patients.
     Priority order: renal override > oncology overlay > geriatric overlay >
     pediatric > ICU > ICU-obese override > region/context default. */
  var proteinW = Wraw;
  var protKey;
  if (renalStatus === 'ckd_nondialysis' || (context === 'renal' && renalStatus === 'none')) protKey = 'renal';
  else if (renalStatus === 'hemodialysis') protKey = context === 'icu' ? 'renal_dialysis_icu' : 'renal_dialysis';
  else if (renalStatus === 'aki_nondialysis') protKey = context === 'icu' ? 'renal_aki_icu' : 'renal_aki';
  else if (renalStatus === 'crrt') protKey = 'renal_crrt';
  else if (oncoState === 'sarcopenia') protKey = 'oncology_sarcopenia';
  else if (oncoState === 'cachexia') protKey = 'oncology_cachexia';
  else if (oncoState === 'standard') protKey = 'oncology';
  else if (gerState !== 'none' || (A >= 65 && context !== 'icu')) protKey = 'geriatric';
  /* Pediatric patients require age-band specific targets that differ markedly
     from adult ambulatory 0.8 g/kg — catch before the generic context fallback. */
  else if ((region === 'pediatric' || A < 18) && context !== 'icu') {
    if      (A < 1)  protKey = 'pediatric_infant';
    else if (A < 4)  protKey = 'pediatric_toddler';
    else if (A < 13) protKey = 'pediatric_child';
    else             protKey = 'pediatric_adolescent';
    flags.push({ type: 'info', msg: 'Paediatric protein target: ' + PROTEIN_TARGETS[protKey].rec + ' g/kg/day (' + PROTEIN_TARGETS[protKey].src + ') applied for age ' + A.toFixed(1) + ' years. Verify with paediatric dietitian.' });
  }
  else if (region === 'india' && context === 'ambulatory') protKey = 'india_ambulatory';
  else if (context === 'icu') protKey = val('mep-protein-protocol') === 'aspen' ? 'icu_aspen' : 'icu_espen';
  else if (PROTEIN_TARGETS[context]) protKey = context;
  else protKey = 'ambulatory';
  var protTarget = PROTEIN_TARGETS[protKey];
  var proteinBasisLabel = 'Actual body weight ' + Wraw.toFixed(1) + ' kg';
  /* KDOQI 2020 specifies ideal body weight for metabolically stable MHD/PD. */
  if (protKey === 'renal_dialysis' && IBW > 0) {
    proteinW = IBW;
    proteinBasisLabel = 'Ideal body weight ' + IBW.toFixed(1) + ' kg (KDOQI stable MHD/PD)';
  }
  if ((protKey === 'renal_dialysis_icu' || protKey === 'renal_aki_icu' || protKey === 'renal_crrt') && IBW > 0) {
    var renalUsualWeight = numVal('mep-usual-weight');
    if (renalUsualWeight > 0) {
      proteinW = renalUsualWeight;
      proteinBasisLabel = 'Pre-hospital / usual weight ' + renalUsualWeight.toFixed(1) + ' kg (acute kidney disease)';
    } else if (dryWeight > 0) {
      proteinW = dryWeight;
      proteinBasisLabel = 'Clinician-verified dry weight ' + dryWeight.toFixed(1) + ' kg (acute kidney disease)';
    } else {
      proteinW = IBW;
      proteinBasisLabel = 'Ideal body weight ' + IBW.toFixed(1) + ' kg fallback — enter usual or verified dry weight if available';
      flags.push({ type: 'warn', msg: 'Acute/critical kidney disease: usual pre-hospital or clinician-verified dry weight is preferred for protein prescription. Neither was entered, so IBW is used as a documented fallback; actual fluid-shifted weight is not used.' });
    }
  }
  /* ICU obesity differs by selected protocol. ESPEN uses 1.3 g/kg adjusted
     body weight when urinary nitrogen/LBM guidance is unavailable; ASPEN/SCCM
     uses the higher IBW-based obesity tiers. */
  if (context === 'icu' && bmi >= 30 && IBW > 0) {
    if (val('mep-protein-protocol') === 'aspen' && (protKey === 'icu_aspen' || protKey === 'icu')) {
      proteinW = IBW;
      proteinBasisLabel = 'Ideal body weight ' + IBW.toFixed(1) + ' kg (ASPEN/SCCM ICU obesity)';
      protKey = bmi >= 40 ? 'icu_obese_morbid' : 'icu_obese';
      protTarget = PROTEIN_TARGETS[protKey];
    } else if (protKey === 'icu_espen' || protKey === 'icu') {
      proteinW = computeAdjBW(Wraw, IBW);
      proteinBasisLabel = 'Adjusted body weight ' + proteinW.toFixed(1) + ' kg (ESPEN ICU obesity)';
    }
  }
  /* [#4] Explicit weight-basis override for protein dosing (wins over auto logic).
     Energy equations keep their guideline weight; only protein dosing weight changes. */
  if (proteinBasisSel !== 'auto') {
    var adjBWval = computeAdjBW(Wraw, IBW);
    if (proteinBasisSel === 'actual')        { proteinW = Wraw;     proteinBasisLabel = 'Actual body weight ' + Wraw.toFixed(1) + ' kg (manual)'; }
    else if (proteinBasisSel === 'ideal')    { proteinW = IBW;      proteinBasisLabel = 'Ideal body weight ' + IBW.toFixed(1) + ' kg (manual)'; }
    else if (proteinBasisSel === 'adjusted') { proteinW = adjBWval; proteinBasisLabel = 'Adjusted body weight ' + adjBWval.toFixed(1) + ' kg (manual)'; }
    else if (proteinBasisSel === 'dry') {
      if (dryWeight > 0) { proteinW = dryWeight; proteinBasisLabel = 'Dry / euvolemic weight ' + dryWeight.toFixed(1) + ' kg (manual)'; }
      else { flags.push({ type: 'warn', msg: 'Protein basis set to dry weight, but no dry weight was entered — using actual weight ' + Wraw.toFixed(1) + ' kg. Enter a dry/euvolemic weight in the Body Composition tab.' }); }
    }
    else if (proteinBasisSel === 'post_dialysis') {
      var postDialysisWeight = numVal('mep-post-dialysis-weight');
      if (postDialysisWeight > 0) { proteinW = postDialysisWeight; proteinBasisLabel = 'Post-dialysis weight ' + postDialysisWeight.toFixed(1) + ' kg (manual)'; }
      else { flags.push({ type: 'warn', msg: 'Protein basis set to post-dialysis weight, but no post-dialysis weight was entered — using actual weight ' + Wraw.toFixed(1) + ' kg.' }); }
    }
    flags.push({ type: 'info', msg: 'Protein dosing weight basis manually set to ' + proteinBasisLabel + '. Energy equations retain their guideline weight basis.' });
  } else if (dryWeight > 0 && renalStatus !== 'none') {
    flags.push({ type: 'info', msg: 'Dry weight ' + dryWeight.toFixed(1) + ' kg entered for a renal/dialysis patient. For fluid-overloaded patients, consider setting the protein dosing basis to Dry weight (Body Composition tab).' });
  }
  var protMin = (protTarget.min * proteinW).toFixed(1);
  var protMax = ((parseFloat(protTarget.rec.split('–')[1] || protTarget.rec.replace('>','').replace('<','')) || protTarget.min) * proteinW).toFixed(1);

  /* ── Hospital assessment fields: trend, fluid status, refeeding and delivery ── */
  var usualWeight = numVal('mep-usual-weight');
  var weight1m = numVal('mep-weight-1m');
  var weight3m = numVal('mep-weight-3m');
  var weight6m = numVal('mep-weight-6m');
  var fluidStatus = val('mep-fluid-status') || 'none';
  var refeedingRisk = val('mep-refeeding-risk') || 'none';
  function weightLossPct(previous) { return previous > 0 ? ((previous - Wraw) / previous) * 100 : 0; }
  var trendFlags = [];
  if (usualWeight > 0 && weightLossPct(usualWeight) >= 5) trendFlags.push(weightLossPct(usualWeight).toFixed(1) + '% from usual weight');
  if (weight1m > 0 && weightLossPct(weight1m) >= 5) trendFlags.push(weightLossPct(weight1m).toFixed(1) + '% in 1 month');
  if (weight3m > 0 && weightLossPct(weight3m) >= 7.5) trendFlags.push(weightLossPct(weight3m).toFixed(1) + '% in 3 months');
  if (weight6m > 0 && weightLossPct(weight6m) >= 10) trendFlags.push(weightLossPct(weight6m).toFixed(1) + '% in 6 months');
  if (trendFlags.length) flags.push({ type: 'warn', msg: 'Significant unintentional weight loss: ' + trendFlags.join('; ') + '. Review intake, muscle mass, inflammation, function and formal malnutrition criteria.' });
  if (fluidStatus !== 'none') {
    flags.push({ type: 'warn', msg: selectedLabel('mep-fluid-status') + '. Current scale weight may be misleading. No dry weight has been mathematically invented; use a clinically verified dry/euvolemic weight or the approved hospital protocol.' });
    if (dryWeight <= 0) flags.push({ type: 'warn', msg: 'Fluid disturbance recorded without a dry/euvolemic weight. Confirm a clinically verified dosing weight before final prescription.' });
  }
  if (val('mep-dialysis-recent') === 'yes') flags.push({ type: 'info', msg: 'Recent dialysis recorded. Interpret the weight date and source in relation to the dialysis session and fluid balance.' });
  if (refeedingRisk === 'possible') flags.push({ type: 'warn', msg: 'Possible refeeding risk selected. Review local screening criteria, electrolytes, thiamine, fluid status and progressive energy advancement.' });
  if (refeedingRisk === 'high') flags.push({ type: 'critical', msg: 'High refeeding risk selected. Do not begin at the full calculated target; use the approved progressive feeding and biochemical monitoring protocol.' });
  var refeedingTargetWithheld = refeedingRisk === 'high';

  var deliveryEnergy=0,deliveryProtein=0,deliveryEntered=false,deliveryEnergyKnown=true,deliveryProteinKnown=true;
  var deliveryComponents=[];
  function enteredNonnegative(node){
    if(!node||node.value.trim()==='')return null;
    var n=Number(node.value);return Number.isFinite(n)&&n>=0?n:null;
  }
  document.querySelectorAll('.mep-delivery-row').forEach(function(row){
    var labelNode=row.querySelector('label'),kcalNode=row.querySelector('.mep-delivery-kcal'),proteinNode=row.querySelector('.mep-delivery-protein');
    if((!kcalNode||kcalNode.value.trim()==='')&&(!proteinNode||proteinNode.value.trim()===''))return;
    deliveryEntered=true;
    var kcalAmount=enteredNonnegative(kcalNode),proteinAmount=proteinNode&&proteinNode.disabled?0:enteredNonnegative(proteinNode);
    if(kcalAmount===null)deliveryEnergyKnown=false;else deliveryEnergy+=kcalAmount;
    if(proteinAmount===null)deliveryProteinKnown=false;else deliveryProtein+=proteinAmount;
    deliveryComponents.push({source:labelNode?labelNode.textContent.trim():'Nutrition source',energy:kcalAmount,protein:proteinAmount});
  });
  deliveryEnergyKnown=deliveryEntered&&deliveryEnergyKnown;deliveryProteinKnown=deliveryEntered&&deliveryProteinKnown;
  var proteinMidpoint = (parseFloat(protMin) + parseFloat(protMax)) / 2;
  var deliveryTargetUsable = !pediatricTargetWithheld && !refeedingTargetWithheld && !((tier === 1 || tier === 2) && quality.type === 'invalid');
  var deliveryEnergyPct = deliveryTargetUsable && finalTarget > 0 ? (deliveryEnergy / finalTarget) * 100 : 0;
  var deliveryProteinPct = proteinMidpoint > 0 ? (deliveryProtein / proteinMidpoint) * 100 : 0;
  if (deliveryEntered && !deliveryTargetUsable) flags.push({ type: 'info', msg: 'Current intake was recorded, but energy target attainment is withheld because the selected metabolic measurement is not suitable for clinical use.' });
  if (!predictiveWeekOne && deliveryEnergyKnown && deliveryTargetUsable && deliveryEnergyPct < 80) flags.push({ type: 'warn', msg: 'Energy delivery is ' + Math.round(deliveryEnergyPct) + '% of the current target; documented shortfall ' + Math.max(0, Math.round(finalTarget - deliveryEnergy)) + ' kcal/day.' });
  if (predictiveWeekOne && deliveryEnergyKnown) flags.push({type:deliveryEnergy>=finalTarget?'warn':'info',msg:'Recorded delivery is '+(deliveryEnergy>=finalTarget?'at or above':'below')+' the exclusive first-week predictive energy ceiling. Percentage of this ceiling is not target attainment or a feeding deficit.'});
  if (deliveryProteinKnown && deliveryProteinPct < 80) flags.push({ type: 'warn', msg: 'Protein delivery is ' + Math.round(deliveryProteinPct) + '% of the recommended midpoint; documented shortfall ' + Math.max(0, proteinMidpoint - deliveryProtein).toFixed(1) + ' g/day.' });
  if (protKey === 'renal') {
    flags.push({ type: 'warn', msg: 'Renal protein guardrail: CKD/non-dialysis selected. Target 0.6–0.8 g/kg/day (KDOQI 2020 LPD approach) applies to stable CKD Stages 3–5 aiming to slow progression. Do not apply to acutely ill patients — escalate to ICU or dialysis key if appropriate.' });
  } else if (protKey === 'renal_aki') {
    flags.push({ type: 'info', msg: 'AKI without acute/critical illness: ESPEN Kidney 2021 suggests 0.8–1.0 g/kg/day. Reclassify if the patient is catabolic or critically ill.' });
  } else if (protKey === 'renal_aki_icu') {
    flags.push({ type: 'info', msg: 'Acute/critical illness with AKI and no KRT: start near 1.0 g/kg/day and advance toward 1.3 g/kg/day if tolerated (ESPEN Kidney 2021).' });
  } else if (protKey === 'renal_dialysis') {
    flags.push({ type: 'info', msg: 'Stable maintenance dialysis: KDOQI 2020 target 1.0–1.2 g/kg ideal body weight/day. Acute illness, wounds, protein-energy wasting, or local renal-unit protocols may require individual adjustment.' });
  } else if (protKey === 'renal_dialysis_icu' || protKey === 'renal_crrt') {
    flags.push({ type: 'info', msg: 'Renal protein status selected: ' + protTarget.src + '. Protein target adjusted separately from generic renal context.' });
  }
  if (val('mep-protein-protocol') === 'local') flags.push({ type: 'warn', msg: 'Local protein protocol was selected, but no local numeric rule is configured. The displayed ESPEN/context result is a reference only and requires local-protocol confirmation.' });
  if (context === 'icu' && val('mep-icu-protocol') === 'local') flags.push({ type: 'warn', msg: 'Local ICU progression was selected, but no local phase percentages are configured. The displayed 70%/90% DietXP defaults remain reference values only.' });
  if (renalStatus !== 'none' && val('mep-renal-protocol') === 'local') flags.push({ type: 'warn', msg: 'Local renal protocol was selected, but no local protein coefficients are configured. Confirm the displayed guideline reference against the renal unit protocol.' });
  /* Flag when energy weight and protein weight differ (obese) */
  if (bmi >= 30 && context === 'icu') {
    if (val('mep-protein-protocol') === 'aspen') {
      var oBMITier = bmi >= 40 ? '≥40: target 2.5 g/kg IBW' : '30–39.9: target 2.0–2.5 g/kg IBW';
      flags.push({ type: 'info', msg: 'ICU obesity, ASPEN/SCCM protocol (BMI ' + oBMITier + '): protein is dosed on IBW ' + IBW.toFixed(1) + ' kg. Energy equations use ' + (W !== Wraw ? 'AdjBW ' + W.toFixed(1) : 'actual weight ' + Wraw.toFixed(1)) + ' kg.' });
    } else {
      flags.push({ type: 'info', msg: 'ICU obesity, ESPEN protocol: 1.3 g/kg/day delivered progressively using adjusted body weight ' + proteinW.toFixed(1) + ' kg when urinary nitrogen loss or measured lean mass is unavailable.' });
    }
  } else if (W !== Wraw) {
    flags.push({ type: 'info', msg: 'Weight basis: energy equations use AdjBW ' + W.toFixed(1) + ' kg; protein targets use actual weight ' + Wraw.toFixed(1) + ' kg. This is per ASPEN obesity guidelines.' });
  }

  /* ── RENDER RESULTS ── */
  el('ic-results-placeholder').style.display = 'none';
  var card = el('ic-results-card');
  card.classList.add('visible');
  card.style.display = 'block';

  var tierLabel = tier === 1 ? 'Tier 1 - Full IC Reference Method' : tier === 2 ? 'Tier 2 - VCO₂/VO₂ ICU Mode' : 'Tier 3 - Predictive';
  setHTML('ic-method-badge', tierLabel);
  el('ic-method-badge').style.background = tier === 1 ? 'rgba(16,185,129,.15)' : tier === 2 ? 'rgba(29,184,154,.15)' : 'rgba(245,158,11,.12)';
  el('ic-method-badge').style.color = tier === 1 ? '#065f46' : tier === 2 ? '#1db89a' : '#92400e';
  el('ic-method-badge').style.borderColor = tier === 1 ? 'rgba(16,185,129,.25)' : tier === 2 ? 'rgba(29,184,154,.25)' : 'rgba(245,158,11,.2)';

  setText('ic-ree-val', Math.round(REE).toLocaleString('en-IN'));
  setText('ic-ree-sub', method);
  setText('ic-tdee-val', Math.round(TDEE).toLocaleString('en-IN'));
  setText('ic-tdee-label', context === 'icu' ? 'Energy before ICU phase adjustment' : 'Estimated daily energy before clinical adjustment');
  setText('ic-pal-sub', palLabel);

  setText('ic-bmi-val', bmi.toFixed(1));
  setText('ic-bmi-cat', bmiCategory(bmi, region));

  setText('ic-final-val', energyDisplay);

  /* [#3] Result clarity: label REE measured vs estimated; annotate the target */
  var primaryEl = el('ic-metric-primary');
  if (primaryEl) {
    primaryEl.classList.remove('measured', 'estimated');
    primaryEl.classList.add(tier === 1 ? 'measured' : 'estimated');
  }
  setText('ic-ree-label', (tier === 1 ? 'Measured' : 'Estimated') + ' Resting Energy Expenditure (REE)');
  var rangeEl = el('ic-final-range');
  if (rangeEl) {
    var rangeStr = '';
    if (oncoState !== 'none' && oncoMin) {
      rangeStr = 'Guideline range ' + Math.round(oncoMin).toLocaleString('en-IN') + '–' + Math.round(oncoMax).toLocaleString('en-IN') + ' kcal/day';
    } else if (espenMin && espenMax && Math.round(espenMin) !== Math.round(espenMax)) {
      rangeStr = 'ESPEN range ' + Math.round(espenMin).toLocaleString('en-IN') + '–' + Math.round(espenMax).toLocaleString('en-IN') + ' kcal/day';
    }
    rangeEl.textContent = rangeStr;
    rangeEl.style.display = rangeStr ? 'block' : 'none';
  }
  var noteEl = el('ic-final-note');
  if (noteEl) {
    noteEl.textContent = (targetPhaseLabel && targetPhaseLabel !== 'No phase adjustment')
      ? targetPhaseLabel
      : 'After feeding phase, disease status & clinician judgement.';
  }

  /* [#13] Explainable confidence — factor list shown under the confidence label */
  var factors = [];
  if (tier === 1) {
    var rqm = vco2_L / vo2;
    factors.push({ text: 'Full VO₂ + VCO₂ measurement (Weir)', level: 'good' });
    factors.push({ text: 'Measured RQ ' + rqm.toFixed(2), level: (rqm < 0.67 || rqm > 1.30) ? 'bad' : (rqm < 0.70 || rqm > 1.00) ? 'warn' : 'good' });
    factors.push({ text: calibration === 'failed' ? 'Calibration FAILED' : calibration === 'not_performed' ? 'Calibration not documented' : 'Calibration documented', level: calibration === 'failed' ? 'bad' : calibration === 'not_performed' ? 'warn' : 'good' });
    factors.push({ text: steadyState === 'yes' ? 'Steady state achieved' : steadyState === 'no' ? 'Steady state NOT achieved' : 'Steady state not assessed', level: steadyState === 'yes' ? 'good' : steadyState === 'no' ? 'bad' : 'warn' });
    factors.push({ text: airLeak === 'none' ? 'No circuit leak recorded' : airLeak === 'confirmed' ? 'Confirmed air leak' : 'Suspected air leak', level: airLeak === 'none' ? 'good' : airLeak === 'confirmed' ? 'bad' : 'warn' });
    if (measDuration > 0) factors.push({ text: 'Analysed interval ' + measDuration + ' min; CV limit ' + (measDuration >= 25 ? '≤10%' : measDuration >= 5 ? '≤5%' : 'not met'), level: measDuration < 5 ? 'bad' : 'good' });
    if (val('ic-vo2-cv') !== '') factors.push({ text: 'VO₂ CV ' + vo2Cv.toFixed(1) + '%', level: (measDuration >= 25 ? vo2Cv > 10 : vo2Cv > 5) ? 'bad' : 'good' });
    if (val('ic-vco2-cv') !== '') factors.push({ text: 'VCO₂ CV ' + vco2Cv.toFixed(1) + '%', level: (measDuration >= 25 ? vco2Cv > 10 : vco2Cv > 5) ? 'bad' : 'good' });
    if (fio2 > 0) factors.push({ text: 'FiO₂ ' + fio2.toFixed(2) + (fio2DeviceMax > 0 ? ' · device limit ' + fio2DeviceMax.toFixed(2) : ' · device limit not documented'), level: fio2DeviceMax > 0 && fio2 > fio2DeviceMax ? 'bad' : fio2DeviceMax > 0 && fio2 < fio2DeviceMax - 0.05 ? 'good' : 'warn' });
  } else if (tier === 2 && vco2_mL > 0) {
    factors.push({ text: 'VCO₂-only estimate (no measured VO₂)', level: 'warn' });
    factors.push({ text: 'Ventilator-derived VCO₂', level: 'warn' });
    factors.push({ text: 'Assumed RQ ' + rq.toFixed(2) + ' → ×' + EQ.vcoMultiplier(rq).toFixed(2), level: (rq < 0.67 || rq > 1.30) ? 'bad' : (rq < 0.70 || rq > 1.00) ? 'warn' : 'good' });
    if (measDuration > 0) factors.push({ text: 'Analysed interval ' + measDuration + ' min', level: measDuration < 5 ? 'bad' : 'good' });
    if (val('ic-vco2-cv') !== '') factors.push({ text: 'VCO₂ CV ' + vco2Cv.toFixed(1) + '%', level: (measDuration >= 25 ? vco2Cv > 10 : vco2Cv > 5) ? 'bad' : 'good' });
  } else if (tier === 2 && vo2Only > 0) {
    factors.push({ text: 'VO₂-only fallback — VCO₂ estimated from RQ', level: 'warn' });
    factors.push({ text: 'Assumed RQ ' + rqVo2Only.toFixed(2), level: (rqVo2Only < 0.67 || rqVo2Only > 1.30) ? 'bad' : (rqVo2Only < 0.70 || rqVo2Only > 1.00) ? 'warn' : 'good' });
  } else {
    factors.push({ text: 'Predictive equation — no gas-exchange measurement', level: 'warn' });
    factors.push({ text: method.split(' (')[0].split(' ×')[0], level: '' });
    if (stressFactor > 1.0) factors.push({ text: 'Stress factor ×' + stressFactor + ' applied', level: 'warn' });
  }
  quality.factors = factors;

  /* [#2] Safety gate: an invalid measurement pathway must not output a confident
     target. Show the locked panel, list the critical reasons, and withhold the
     daily energy target until the clinician resolves them or picks another route. */
  var lockPanel = el('ic-safety-lock');
  var blocked = pediatricTargetWithheld || refeedingTargetWithheld || ((tier === 1 || tier === 2) && quality.type === 'invalid');
  if (lockPanel) {
    if (blocked) {
      var critMsgs = flags.filter(function(f){ return f.type === 'critical'; }).map(function(f){ return f.msg; });
      if (critMsgs.length === 0) critMsgs = [quality.reason];
      setHTML('ic-safety-lock-reasons', critMsgs.map(function(m){ return '<li>' + m + '</li>'; }).join(''));
      lockPanel.hidden = false;
      setText('ic-final-val', 'Withheld');
      setText('ic-safety-lock-heading', pediatricTargetWithheld ? 'Paediatric prescription protocol required' : refeedingTargetWithheld ? 'Full energy target withheld because high refeeding risk is recorded' : 'Measurement not suitable for a definitive REE result');
      var altVco = el('ic-safety-vco2'); if (altVco) altVco.hidden = pediatricTargetWithheld || refeedingTargetWithheld;
      var altPredictive = el('ic-safety-predictive'); if (altPredictive) altPredictive.hidden = pediatricTargetWithheld || refeedingTargetWithheld;
      var reviewButton = el('ic-safety-review'); if (reviewButton) reviewButton.textContent = refeedingTargetWithheld ? 'Review refeeding risk and local protocol' : pediatricTargetWithheld ? 'Review paediatric pathway' : 'Review measurement checklist';
      var frEl = el('ic-final-range'); if (frEl) frEl.style.display = 'none';
      var fnEl = el('ic-final-note'); if (fnEl) fnEl.textContent = 'Definitive target withheld - resolve the flagged measurement issues, then recalculate or use an alternative pathway.';
      setText('ic-ree-label', pediatricTargetWithheld ? (tier === 1 ? 'Measured' : 'Estimated') + ' REE — paediatric target withheld' : (tier === 1 ? 'Measured' : 'Estimated') + ' REE - NOT VALIDATED');
    } else {
      lockPanel.hidden = true;
      var altVcoReset = el('ic-safety-vco2'); if (altVcoReset) altVcoReset.hidden = false;
      var altPredictiveReset = el('ic-safety-predictive'); if (altPredictiveReset) altPredictiveReset.hidden = false;
    }
  }

  setQualityStatus(quality);
  setResultTransparency({
    method: method,
    formula: formulaExpression,
    inputs: formulaInputs,
    assumptions: formulaAssumptions,
    targetPhase: targetPhaseLabel,
    proteinBasis: proteinBasisLabel + ' · ' + protTarget.rec + ' g/kg/day'
  });

  /* ── Measured-vs-Predicted panel - item 5 ── */
  var mvpPanel = el('ic-mvp-panel');
  var mvpRowsEl = el('ic-mvp-rows');
  if ((tier === 1 || tier === 2) && compRows.length > 0 && mvpPanel && mvpRowsEl) {
    mvpPanel.classList.add('visible');
    mvpRowsEl.innerHTML = '';
    compRows.forEach(function(r) {
      var diff = REE - r.ree;
      var pct = (diff / r.ree * 100);
      var diffClass = Math.abs(pct) <= 5 ? 'close' : pct > 0 ? 'over' : 'under';
      var diffLabel = (pct > 0 ? '+' : '') + pct.toFixed(1) + '%';
      mvpRowsEl.innerHTML +=
        '<div class="ic-mvp-row">' +
          '<div class="ic-mvp-name">' + r.name + '</div>' +
          '<div class="ic-mvp-val">' + r.ree.toLocaleString('en-IN') + ' kcal</div>' +
          '<div class="ic-mvp-diff ' + diffClass + '">' + diffLabel + '</div>' +
        '</div>';
    });
  } else {
    if (mvpPanel) mvpPanel.classList.remove('visible');
  }

  /* ── ICMR-NIN Reference Band - item 10 ── */
  var icmrBand = el('ic-icmr-band');
  var icmrBandBody = el('ic-icmr-band-body');
  if (icmrBand && icmrBandBody && A >= 18) {
    /* ICMR-NIN 2020 Schofield-W base reference ranges (±15% of ICMR corrected value) */
    var icmrRef = EQ.icmrNIN2020(Wraw, A, sex);
    var icmrLow = Math.round(icmrRef * 0.85);
    var icmrHigh = Math.round(icmrRef * 1.15);
    var ageGroup = A < 30 ? '18–29 yrs' : A < 60 ? '30–59 yrs' : '60+ yrs';
    var sexLabel = sex === 'M' ? 'Male' : 'Female';
    var corrPct = sex === 'M' ? '10%' : '9%';
    icmrBand.classList.add('visible');
    icmrBandBody.innerHTML =
      '<div class="ic-icmr-band-row"><span class="ic-icmr-band-label">Age group</span><span class="ic-icmr-band-value">' + ageGroup + ' · ' + sexLabel + '</span></div>' +
      '<div class="ic-icmr-band-row"><span class="ic-icmr-band-label">ICMR-NIN REE reference</span><span class="ic-icmr-band-value">' + Math.round(icmrRef) + ' kcal/day</span></div>' +
      '<div class="ic-icmr-band-row"><span class="ic-icmr-band-label">Expected range (±15%)</span><span class="ic-icmr-band-value">' + icmrLow + '–' + icmrHigh + ' kcal/day</span></div>' +
      '<div class="ic-icmr-band-row"><span class="ic-icmr-band-label">Indian phenotype correction</span><span class="ic-icmr-band-value">−' + corrPct + ' (Schofield W-only base)</span></div>' +
      '<div class="ic-icmr-band-row"><span class="ic-icmr-band-label">Patient REE</span><span class="ic-icmr-band-value ic-icmr-band-patient">' + Math.round(REE) + ' kcal/day' + (REE >= icmrLow && REE <= icmrHigh ? ' ✓ Within range' : REE < icmrLow ? ' ↓ Below reference' : ' ↑ Above reference') + '</span></div>';
  }

  /* Additions */
  var addWrap = el('ic-additions-wrap');
  addWrap.innerHTML = '';
  addList.forEach(function(a) {
    if (a.range) {
      /* Oncology / range-type entries */
      addWrap.innerHTML += '<div class="ic-addition-row">' +
        '<span class="ic-addition-label">⚕ ' + a.label + '</span>' +
        '<span class="ic-addition-value">' + a.range + '</span>' +
        '</div>';
    } else {
      addWrap.innerHTML += '<div class="ic-addition-row">' +
        '<span class="ic-addition-label">+ ' + a.label + '</span>' +
        '<span class="ic-addition-value">+' + a.val + ' kcal</span>' +
        '</div>';
    }
  });

  /* Flags — use inline SVG icons for consistent sizing (Bug-7 design fix) */
  var flagsWrap = el('ic-flags-wrap');
  flagsWrap.innerHTML = '';
  var flagIconSVG = {
    critical: '<svg width="10" height="10" fill="none" viewBox="0 0 10 10"><path d="M2 2l6 6M8 2l-6 6" stroke="white" stroke-width="1.8" stroke-linecap="round"/></svg>',
    warn:     '<svg width="10" height="10" fill="none" viewBox="0 0 10 10"><path d="M5 3v2.5M5 7.5h.01" stroke="white" stroke-width="1.8" stroke-linecap="round"/></svg>',
    success:  '<svg width="10" height="10" fill="none" viewBox="0 0 10 10"><path d="M2 5.5l2 2 4-4" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    info:     '<svg width="10" height="10" fill="none" viewBox="0 0 10 10"><path d="M5 4.5V7M5 2.5h.01" stroke="white" stroke-width="1.8" stroke-linecap="round"/></svg>'
  };
  flags.forEach(function(f) {
    var fClass = 'flag-' + f.type;
    var iconSVG = flagIconSVG[f.type] || flagIconSVG.info;
    flagsWrap.innerHTML +=
      '<div class="ic-flag ' + fClass + '">' +
        '<div class="ic-flag-icon">' + iconSVG + '</div>' +
        '<div>' + f.msg + '</div>' +
      '</div>';
  });

  /* Protein bar — fix Bug-7: open-ended rec strings (e.g. '>1.2') should not
     show the same value as Min. Detect the '>' prefix and label correctly. */
  var isOpenEnded = protTarget.rec.charAt(0) === '>';
  setText('ic-protein-range', protTarget.rec + ' g/kg/day');
  setText('ic-protein-min', 'Min: ' + protMin + ' g/day');
  setText('ic-protein-rec', isOpenEnded ? ('Target: ' + protTarget.rec + ' g/kg/day') : ('Rec: ' + protMax + ' g/day'));
  setText('ic-protein-src', protTarget.src);
  setText('ic-protein-basis', 'Basis: ' + proteinBasisLabel);
  var pctFill = Math.min((protTarget.min / 2.5) * 100, 100);
  setTimeout(function() {
    var fill = el('ic-protein-bar-fill');
    if (fill) fill.style.width = pctFill + '%';
  }, 200);

  /* Macros — suppressed when the energy target is withheld (#2), so a blocked
     measurement never shows an energy-derived macro prescription. */
  if (blocked) {
    ['mc-protein','mc-fat','mc-carb'].forEach(function(id){ setText(id, '—'); });
    ['mc-protein-kcal','mc-fat-kcal','mc-carb-kcal'].forEach(function(id){ setText(id, '— kcal'); });
  } else {
    updateMacros(finalTarget);
  }

  /* Comparison table */
  var compWrap = el('ic-comparison-wrap');
  var compRowsEl = el('ic-comparison-rows');
  if (compRows.length > 0) {
    compWrap.style.display = 'block';
    compRowsEl.innerHTML = '';
    compRows.forEach(function(r) {
      var rowClass = r.isSelected ? 'ic-comparison-row is-active' : 'ic-comparison-row';
      var tagHtml = '';
      if (r.tag === 'legacy') tagHtml = '<span class="ic-legacy-tag">Legacy</span>';
      if (r.tag === 'india' || r.tag === 'selected' && r.name.indexOf('ICMR') === 0) tagHtml = '<span class="ic-india-tag">India</span>';
      compRowsEl.innerHTML +=
        '<div class="' + rowClass + '">' +
          '<div class="ic-comparison-name">' + r.name + tagHtml + '</div>' +
          '<div class="ic-comparison-right">' +
            '<div class="ic-comparison-ree">' + r.ree.toLocaleString('en-IN') + ' <small style="font-size:.65rem;font-family:var(--ic-font-body);color:var(--ic-text-muted);">kcal</small></div>' +
            '<div class="ic-comparison-delta">' + r.delta + '</div>' +
          '</div>' +
        '</div>';
    });
  } else {
    compWrap.style.display = 'none';
  }

  /* ── One authoritative result object for every output surface ── */
  var statusTitle, statusCopy, statusType;
  if (blocked) {
    statusTitle = pediatricTargetWithheld ? 'Paediatric target withheld' : refeedingTargetWithheld ? 'Starting target withheld — high refeeding risk' : 'Not suitable for clinical use';
    statusCopy = pediatricTargetWithheld
      ? 'REE is shown for audit, but the final paediatric prescription is withheld. Use an approved age-specific paediatric protocol and specialist review.'
      : refeedingTargetWithheld
      ? 'The calculated full requirement remains in the audit, but the current starting target is withheld. Apply the approved refeeding protocol, electrolyte/thiamine plan, and monitoring schedule.'
      : 'A definitive energy target is withheld. Resolve the failed validity checks, repeat the measurement, or use an alternative pathway.';
    statusType = 'invalid';
  } else if (tier === 1) {
    statusTitle = 'Direct measured result';
    statusCopy = quality.type === 'valid'
      ? 'Full indirect calorimetry meets the entered quality checks and is suitable for clinical interpretation.'
      : 'Full indirect calorimetry was completed, but the recorded quality limitations require clinical review.';
    statusType = quality.type === 'valid' ? 'valid' : 'caution';
  } else if (tier === 2 && vco2_mL > 0 && quality.type !== 'invalid' && quality.type !== 'caution') {
    statusTitle = 'Gas-derived estimate';
    statusCopy = 'A ventilator-derived estimate is available. It is not equivalent to full indirect calorimetry; the assumed respiratory quotient and measurement checks remain visible below.';
    statusType = 'estimate';
  } else {
    statusTitle = 'Estimate with limitations';
    statusCopy = tier === 2
      ? 'The gas-derived estimate depends on an assumed respiratory quotient or incomplete gas measurement.'
      : 'A predictive equation was used because direct gas-exchange measurement was unavailable.';
    statusType = 'caution';
  }
  setText('ic-quality-status-value', statusTitle);

  var adjustmentFactor = parseFloat(val('mep-adjbw-factor')) || 0.25;
  var workedCalculation = buildWorkedCalculation({
    tier: tier, vo2: vo2, vco2: vco2_L, un: UN, vco2mL: vco2_mL,
    rq: rq, vo2Only: vo2Only, rqVo2: rqVo2Only, method: method,
    lbm: LBM, sex: sex, age: A, weight: W, height: Hcm,
    stress: stressFactor, ree: REE
  });
  var targetSteps = [];
  targetSteps.push('REE ' + REE.toFixed(1) + ' × assumed whole-day PAL ' + palVal.toFixed(2) + ' = ' + TDEE.toFixed(1) + ' kcal/day');
  if (predictiveWeekOne) targetSteps.push('Energy must remain < '+TDEE.toFixed(1)+' × 0.70 = '+finalTarget.toFixed(1)+' kcal/day, exclusive ceiling; no fixed percentage prescribed');
  else if (context === 'icu' && icuPhase === 'acute') targetSteps.push(TDEE.toFixed(1) + ' × 0.70 = ' + (TDEE * 0.70).toFixed(1) + ' kcal/day acute-phase target');
  else if (context === 'icu' && icuPhase === 'stable') targetSteps.push(TDEE.toFixed(1) + ' × 0.90 = ' + (TDEE * 0.90).toFixed(1) + ' kcal/day stabilisation target');
  else if (context === 'icu' && icuPhase === 'recovery') targetSteps.push(TDEE.toFixed(1) + ' × 1.00 = ' + TDEE.toFixed(1) + ' kcal/day recovery target');
  if (oncoState !== 'none' && tier === 3 && oncoMin) targetSteps.push('Oncology range minimum applied: ' + oncoMin.toFixed(1) + ' kcal/day');
  else if (espenMin && tier === 3) targetSteps.push('Geriatric kcal/kg minimum applied: ' + espenMin.toFixed(1) + ' kcal/day');
  if (pregAdd + lactAdd > 0) targetSteps.push('Clinical increment: +' + (pregAdd + lactAdd) + ' kcal/day');
  targetSteps.push((predictiveWeekOne ? 'Exclusive ceiling = ' : 'Unrounded current target = ') + finalTarget.toFixed(1) + ' kcal/day; displayed '+(predictiveWeekOne?'ceiling':'target')+' = ' + energyDisplay + ' kcal/day');
  var targetCalculation = targetSteps.join(' → ');

  var proteinUpperRate = parseFloat(protTarget.rec.split('–')[1] || protTarget.rec.replace('>','').replace('<','')) || protTarget.min;
  var proteinCalculation = {
    protocol: protTarget.src,
    rateRange: protTarget.rec + ' g/kg/day',
    selectedWeight: proteinW,
    selectedWeightLabel: proteinBasisLabel,
    minimum: protTarget.min.toFixed(2) + ' g/kg × ' + proteinW.toFixed(1) + ' kg = ' + parseFloat(protMin).toFixed(1) + ' g/day',
    maximum: proteinUpperRate.toFixed(2) + ' g/kg × ' + proteinW.toFixed(1) + ' kg = ' + parseFloat(protMax).toFixed(1) + ' g/day',
    midpoint: '(' + parseFloat(protMin).toFixed(1) + ' + ' + parseFloat(protMax).toFixed(1) + ') ÷ 2 = ' + proteinMidpoint.toFixed(1) + ' g/day',
    progression: context === 'icu' ? 'Deliver progressively according to phase, tolerance, renal function, catabolic losses, and local protocol.' : 'Individualise to diagnosis, metabolic stability, intake, and local protocol.'
  };

  var steadyLimit = measDuration >= 25 ? 10 : (measDuration >= 5 ? 5 : null);
  var validityChecks = [];
  function addValidityCheck(label, evidence, status, effect) {
    validityChecks.push({ label: label, evidence: evidence, status: status, effect: effect });
  }
  if (tier === 1 || tier === 2) {
    addValidityCheck('Pathway', method, quality.type === 'invalid' ? 'Fail' : 'Recorded', quality.type === 'invalid' ? 'Target withheld' : 'Used in estimate');
    addValidityCheck('Analysed interval', measDuration ? measDuration + ' minutes' : 'Not documented', !measDuration ? 'Review' : measDuration < 5 ? 'Fail' : 'Pass', measDuration < 5 ? 'Target withheld' : 'Determines CV threshold');
    if (tier === 1) addValidityCheck('VO₂ coefficient of variation', val('ic-vo2-cv') === '' ? 'Not documented' : vo2Cv.toFixed(1) + '%; limit ' + (steadyLimit == null ? 'not established' : '≤' + steadyLimit + '%'), val('ic-vo2-cv') === '' ? 'Review' : steadyLimit != null && vo2Cv > steadyLimit ? 'Fail' : 'Pass', 'Supports steady-state validity');
    addValidityCheck('VCO₂ coefficient of variation', val('ic-vco2-cv') === '' ? 'Not documented' : vco2Cv.toFixed(1) + '%; limit ' + (steadyLimit == null ? 'not established' : '≤' + steadyLimit + '%'), val('ic-vco2-cv') === '' ? 'Review' : steadyLimit != null && vco2Cv > steadyLimit ? 'Fail' : 'Pass', 'Supports steady-state validity');
    addValidityCheck('Steady state', selectedLabel('ic-steady-state') || 'Not assessed', steadyState === 'no' ? 'Fail' : steadyState === 'yes' ? 'Pass' : 'Review', steadyState === 'no' ? 'Target withheld' : 'Interpret with CV and duration');
    addValidityCheck('Device calibration / verification', selectedLabel('ic-calibration') || 'Not documented', calibration === 'failed' ? 'Fail' : calibration === 'not_performed' ? 'Review' : 'Pass', calibration === 'failed' ? 'Target withheld' : 'Method traceability');
    addValidityCheck('Circuit / interface leak', selectedLabel('ic-air-leak') || 'Not documented', airLeak === 'confirmed' ? 'Fail' : airLeak === 'suspected' ? 'Review' : 'Pass', airLeak === 'confirmed' ? 'Target withheld' : 'Gas capture accuracy');
    addValidityCheck('Interface', selectedLabel('ic-interface-type') || 'Not specified', interfaceType === 'ecmo' ? 'Fail' : interfaceType === 'none' ? 'Review' : 'Recorded', 'Defines applicability of measured gases');
    if (tier === 1) addValidityCheck('FiO₂ device range', fio2 > 0 ? 'Measured FiO₂ ' + fio2.toFixed(2) + '; device limit ' + (fio2DeviceMax > 0 ? fio2DeviceMax.toFixed(2) : 'not documented') : 'FiO₂ not entered', fio2 > 0 && fio2DeviceMax > 0 && fio2 > fio2DeviceMax ? 'Fail' : fio2 > 0 && !(fio2DeviceMax > 0) ? 'Review' : 'Recorded', 'No universal cut-off assumed');
    if (tier === 1) addValidityCheck('Measured respiratory quotient', (vco2_L / vo2).toFixed(3) + ' (VCO₂ ÷ VO₂)', (vco2_L / vo2) < 0.67 || (vco2_L / vo2) > 1.30 ? 'Fail' : (vco2_L / vo2) < 0.70 || (vco2_L / vo2) > 1.00 ? 'Review' : 'Pass', 'Physiological plausibility check');
    if (tier === 2) addValidityCheck('Assumed respiratory quotient', (vco2_mL > 0 ? rq : rqVo2Only).toFixed(2), quality.type === 'invalid' ? 'Fail' : 'Assumption', 'Directly changes the derived multiplier');
    addValidityCheck('Protocol disturbances', disturbances.length ? disturbances.join(', ') : 'None recorded', disturbances.length ? 'Review' : 'Pass', disturbances.length ? 'Consider repeat measurement' : 'No adjustment');
  } else {
    addValidityCheck('Predictive pathway', method, 'Estimate', 'No gas-exchange validity claim');
    addValidityCheck('Equation applicability', A < 18 ? 'Paediatric equation required' : 'Adult equation selected', 'Review', 'Interpret within the population used to derive the equation');
  }

  var appliedModifiers = [];
  var ignoredModifiers = [];
  if (context === 'icu') appliedModifiers.push('ICU phase: ' + (targetPhaseLabel || icuPhase));
  if (oncoState !== 'none') appliedModifiers.push('Oncology: ' + selectedLabel('ic-onco-condition'));
  if (gerState !== 'none' || A >= 65) appliedModifiers.push('Geriatric energy/protein interpretation');
  if (renalStatus !== 'none') appliedModifiers.push('Renal protein guardrail: ' + selectedLabel('ic-renal-status'));
  if (pregState !== 'none') appliedModifiers.push('Pregnancy: ' + selectedLabel('ic-pregnancy'));
  if (lactState !== 'none') appliedModifiers.push('Lactation: ' + selectedLabel('ic-lactation'));
  if (refeedingRisk !== 'none') appliedModifiers.push('Refeeding risk: ' + selectedLabel('mep-refeeding-risk'));
  if (fluidStatus !== 'none') appliedModifiers.push('Fluid status: ' + selectedLabel('mep-fluid-status'));
  if (stressFactor > 1 && tier === 3) appliedModifiers.push('Predictive stress factor ×' + stressFactor.toFixed(2));
  if (stressFactor > 1 && tier < 3) ignoredModifiers.push('Stress factor ×' + stressFactor.toFixed(2) + ' ignored because the pathway uses measured/gas-derived energy.');
  if (context === 'icu') ignoredModifiers.push('Outpatient PAL selection ignored; ICU baseline factor fixed at 1.00 before phase progression.');
  if (tier <= 2) ignoredModifiers.push('Predictive-equation weight inputs ignored for energy; the gas equation uses VO₂/VCO₂ data.');
  if (tier === 3 && region !== 'body_comp' && (lbmDirect > 0 || bfPct > 0)) ignoredModifiers.push('Body-composition data retained for audit but did not replace the explicitly selected predictive equation.');

  var macroPreset = MACRO_PRESETS[val('ic-macro-preset')] || MACRO_PRESETS.standard;
  var sourceLabel = selectedLabel('mep-weight-source') || 'Measured today';
  var energyWeightBasis = tier <= 2
    ? (tier === 1 ? 'Measured REE pathway — no dosing weight used for energy' : 'Gas-derived estimate — no body-weight equation used for energy')
    : (region === 'body_comp' ? 'Lean body mass ' + LBM.toFixed(1) + ' kg (' + lbmSource + ')' : ((Math.abs(W - Wraw) > 0.05 ? 'Adjusted body weight ' : 'Actual body weight ') + W.toFixed(1) + ' kg'));
  var deliveryNote = !deliveryEntered
    ? 'No delivery entered.'
    : (!deliveryEnergyKnown||!deliveryProteinKnown?'Delivery is incomplete: blank energy or protein in a recorded route remains unassessed; no missing component is assumed to be zero.':(!deliveryTargetUsable
      ? 'Energy target comparison withheld until a clinically usable metabolic result is available.'
      : (predictiveWeekOne ? 'Energy is evaluated against an exclusive ceiling; protein retains its separate progressive target.' : (deliveryEnergyPct < 80 || deliveryProteinPct < 80
      ? 'Patient remains below the current phase-adjusted target.'
      : 'Documented delivery is at least 80% of the current energy and protein targets.'))));

  if (predictiveWeekOne && deliveryEntered) deliveryNote += ' Energy is compared with an exclusive ceiling, not an assigned delivery target; no shortfall to this ceiling is inferred.';

  var generatedAt = new Date().toLocaleString('en-IN');
  var clinicalResult = {
    generatedAt: generatedAt,
    algorithm: { version: ALGORITHM_VERSION, guidelineReviewDate: GUIDELINE_REVIEW_DATE },
    blocked: blocked,
    blockedReason: pediatricTargetWithheld ? 'paediatric-protocol-required' : refeedingTargetWithheld ? 'high-refeeding-risk' : blocked ? 'measurement-invalid' : null,
    status: { title: statusTitle, copy: statusCopy, type: statusType },
    patient: {
      id: val('mep-patient-id'), sex: sex, age: A, height: Hcm,
      dietitianInitials: val('mep-dietitian-initials'), reviewStatus: selectedLabel('mep-review-status') || 'Draft — requires clinician review', lastEditedAt: generatedAt,
      context: context, contextLabel: selectedLabel('ic-context') || context
    },
    energy: {
      tier: tier, ree: REE, tdee: TDEE, target: finalTarget, operator:energyOperator, upperLimitExclusive:predictiveWeekOne?finalTarget:null, display:energyDisplay,
      expectedTarget: context === 'icu' ? (TDEE + pregAdd + lactAdd) : finalTarget,
      method: method, formula: formulaExpression, inputs: formulaInputs,
      workedCalculation: workedCalculation + ' Target: ' + targetCalculation,
      assumptions: formulaAssumptions, targetPhase: targetPhaseLabel,
      weightBasis: energyWeightBasis, quality: quality
    },
    calculationSteps: [
      { label: 'Method selected', value: method },
      { label: 'Formula', value: formulaExpression },
      { label: 'Patient values substituted', value: formulaInputs },
      { label: 'Arithmetic', value: workedCalculation },
      { label: 'REE result', value: Math.round(REE).toLocaleString('en-IN') + ' kcal/day' },
      { label: 'Clinical target adjustment', value: targetCalculation },
      { label: 'Final clinical target', value: blocked ? 'Withheld pending a valid measurement' : energyDisplay + ' kcal/day' }
    ],
    calculationAudit: {
      energy: {
        method: method,
        formula: formulaExpression,
        substitution: formulaInputs,
        arithmetic: workedCalculation,
        reeUnrounded: REE.toFixed(2) + ' kcal/day',
        targetSteps: targetSteps.slice(),
        finalUnrounded: energyOperator + ' ' + finalTarget.toFixed(2) + ' kcal/day',
        finalDisplayed: blocked ? 'Withheld pending valid measurement' : energyDisplay + ' kcal/day',
        rounding: predictiveWeekOne ? 'Exclusive ceiling retains full precision; display is rounded down to whole kcal.' : 'Intermediate values retain full precision. Energy is rounded to the nearest whole kcal only for display.'
      },
      protein: proteinCalculation,
      validity: validityChecks,
      delivery: {
        components: deliveryComponents.slice(),
        energySum: deliveryComponents.length ? deliveryComponents.map(function(item){ return (item.energy===null?'unassessed':item.energy.toFixed(0)); }).join(' + ') + ' = ' + (deliveryEnergyKnown?deliveryEnergy.toFixed(0):'incomplete; known subtotal '+deliveryEnergy.toFixed(0)) + ' kcal/day' : 'No delivery values entered',
        proteinSum: deliveryComponents.length ? deliveryComponents.map(function(item){ return (item.protein===null?'unassessed':item.protein.toFixed(1)); }).join(' + ') + ' = ' + (deliveryProteinKnown?deliveryProtein.toFixed(1):'incomplete; known subtotal '+deliveryProtein.toFixed(1)) + ' g/day' : 'No delivery values entered',
        energyAchievement: deliveryTargetUsable && deliveryEnergyKnown ? deliveryEnergy.toFixed(0) + ' ÷ ' + finalTarget.toFixed(1) + ' × 100 = ' + deliveryEnergyPct.toFixed(1) + '%' : 'Not calculated',
        proteinAchievement: deliveryProteinKnown ? deliveryProtein.toFixed(1) + ' ÷ ' + proteinMidpoint.toFixed(1) + ' × 100 = ' + deliveryProteinPct.toFixed(1) + '%' : 'Not calculated'
      }
    },
    modifiers: { applied: appliedModifiers, ignored: ignoredModifiers },
    protein: {
      min: parseFloat(protMin), max: parseFloat(protMax), midpoint: proteinMidpoint,
      range: protTarget.rec, weight: proteinW, basis: proteinBasisLabel,
      source: protTarget.src, minRate: protTarget.min, maxRate: proteinUpperRate,
      calculation: proteinCalculation
    },
    weights: {
      actual: Wraw, usual: usualWeight, sourceLabel: sourceLabel,
      history: { '1m': weight1m, '3m': weight3m, '6m': weight6m },
      date: val('mep-current-weight-date'), bmi: bmi, bmiCategory: bmiCategory(bmi, region),
      ibw: adultReferenceWeightApplicable ? IBW : null,
      ibwSource: (val('mep-ibw-protocol') === 'local' && numVal('mep-ibw-override') > 0) ? 'Local clinician-entered override' : 'Devine formula',
      adjusted: adultReferenceWeightApplicable ? computeAdjBW(Wraw, IBW) : null, adjustmentFactor: adjustmentFactor,
      percentIBW: adultReferenceWeightApplicable && IBW > 0 ? (Wraw / IBW) * 100 : null,
      dry: dryWeight, lbm: LBM, lbmSource: lbmSource,
      postDialysis: numVal('mep-post-dialysis-weight'), bodyFatPct: bfPct,
      fatMass: Math.max(0, Wraw - LBM), fluidStatus: fluidStatus,
      amputationNote: val('mep-amputation-note'),
      bodyCompositionMethod: selectedLabel('ic-bc-method') || 'Not provided / estimated'
    },
    weightCalculations: {
      bmi: { formula: 'BMI = weight ÷ height²', substitution: Wraw.toFixed(1) + ' kg ÷ (' + Hm.toFixed(3) + ' m)² = ' + bmi.toFixed(2) + ' kg/m²', value: bmi },
      idealWeight: adultReferenceWeightApplicable ? { formula: val('mep-ibw-protocol') === 'local' && numVal('mep-ibw-override') > 0 ? 'Clinician-entered local IBW' : (sex === 'M' ? 'IBW = 50 + 2.3 × (height inches − 60)' : 'IBW = 45.5 + 2.3 × (height inches − 60)'), substitution: val('mep-ibw-protocol') === 'local' && numVal('mep-ibw-override') > 0 ? IBW.toFixed(1) + ' kg entered' : (sex === 'M' ? '50' : '45.5') + ' + 2.3 × (' + (Hcm / 2.54).toFixed(2) + ' − 60) = ' + IBW.toFixed(2) + ' kg', value: IBW } : null,
      adjustedWeight: adultReferenceWeightApplicable ? { formula: 'AdjBW = IBW + factor × (actual − IBW)', substitution: IBW.toFixed(1) + ' + ' + adjustmentFactor.toFixed(2) + ' × (' + Wraw.toFixed(1) + ' − ' + IBW.toFixed(1) + ') = ' + computeAdjBW(Wraw, IBW).toFixed(2) + ' kg', value: computeAdjBW(Wraw, IBW) } : null,
      percentIBW: adultReferenceWeightApplicable ? { formula: '%IBW = actual ÷ IBW × 100', substitution: Wraw.toFixed(1) + ' ÷ ' + IBW.toFixed(1) + ' × 100 = ' + ((Wraw / IBW) * 100).toFixed(1) + '%', value: IBW > 0 ? (Wraw / IBW) * 100 : 0 } : null,
      leanBodyMass: { formula: bfPct > 0 ? 'LBM = actual × (1 − body-fat fraction)' : lbmDirect > 0 ? 'Clinician-entered measured lean body mass' : (sex === 'M' ? 'Boer LBM = 0.407W + 0.267H − 19.2' : 'Boer LBM = 0.252W + 0.473H − 48.3'), substitution: bfPct > 0 ? Wraw.toFixed(1) + ' × (1 − ' + (bfPct / 100).toFixed(3) + ') = ' + LBM.toFixed(2) + ' kg' : lbmDirect > 0 ? LBM.toFixed(1) + ' kg entered; method ' + selectedLabel('ic-bc-method') : 'W ' + Wraw.toFixed(1) + ' kg; H ' + Hcm.toFixed(1) + ' cm → ' + LBM.toFixed(2) + ' kg', value: LBM },
      fatMass: { formula: 'Fat mass = actual − LBM', substitution: Wraw.toFixed(1) + ' − ' + LBM.toFixed(1) + ' = ' + Math.max(0, Wraw - LBM).toFixed(2) + ' kg', value: Math.max(0, Wraw - LBM) },
      weightHistory: {
        usual: usualWeight > 0 ? { formula: '(previous − current) ÷ previous × 100', substitution: '(' + usualWeight.toFixed(1) + ' − ' + Wraw.toFixed(1) + ') ÷ ' + usualWeight.toFixed(1) + ' × 100 = ' + weightLossPct(usualWeight).toFixed(1) + '%', kgChange: usualWeight - Wraw, percentChange: weightLossPct(usualWeight) } : null,
        '1m': weight1m > 0 ? { formula: '(previous − current) ÷ previous × 100', substitution: '(' + weight1m.toFixed(1) + ' − ' + Wraw.toFixed(1) + ') ÷ ' + weight1m.toFixed(1) + ' × 100 = ' + weightLossPct(weight1m).toFixed(1) + '%', kgChange: weight1m - Wraw, percentChange: weightLossPct(weight1m) } : null,
        '3m': weight3m > 0 ? { formula: '(previous − current) ÷ previous × 100', substitution: '(' + weight3m.toFixed(1) + ' − ' + Wraw.toFixed(1) + ') ÷ ' + weight3m.toFixed(1) + ' × 100 = ' + weightLossPct(weight3m).toFixed(1) + '%', kgChange: weight3m - Wraw, percentChange: weightLossPct(weight3m) } : null,
        '6m': weight6m > 0 ? { formula: '(previous − current) ÷ previous × 100', substitution: '(' + weight6m.toFixed(1) + ' − ' + Wraw.toFixed(1) + ') ÷ ' + weight6m.toFixed(1) + ' × 100 = ' + weightLossPct(weight6m).toFixed(1) + '%', kgChange: weight6m - Wraw, percentChange: weightLossPct(weight6m) } : null
      }
    },
    selectedWeightReasons: {
      energy: tier <= 2 ? 'Gas-exchange data determine REE; body weight is shown for interpretation but does not enter the gas equation.' : region === 'body_comp' ? 'Measured or body-fat-derived lean body mass is the explicit input to the selected body-composition equations.' : (Math.abs(W - Wraw) > 0.05 ? 'Adjusted weight selected for an adult ICU predictive pathway because BMI is ≥30 and actual weight exceeds 130% of Devine IBW.' : 'Actual weight is the input required by the selected predictive equation.'),
      protein: proteinBasisSel !== 'auto' ? 'Clinician-selected dosing basis overrides automatic protocol logic.' : protKey === 'renal_dialysis' ? 'KDOQI 2020 uses ideal body weight for metabolically stable maintenance dialysis.' : (protKey === 'renal_dialysis_icu' || protKey === 'renal_aki_icu' || protKey === 'renal_crrt') ? 'ESPEN Kidney 2021 prefers pre-hospital/usual weight; verified dry weight is used next, with IBW only as a documented fallback.' : (context === 'icu' && bmi >= 30 ? (val('mep-protein-protocol') === 'aspen' ? 'ASPEN/SCCM ICU obesity protocol uses IBW.' : 'ESPEN ICU obesity protocol uses adjusted body weight when nitrogen losses or measured LBM are unavailable.') : 'The selected guideline default for this clinical context is applied.')
    },
    workflowCompleteness: (window.DietXP && window.DietXP.MEP && window.DietXP.MEP.workflowState) ? window.DietXP.MEP.workflowState : null,
    delivery: {
      entered: deliveryEntered, energyKnown:deliveryEnergyKnown,proteinKnown:deliveryProteinKnown,usable: deliveryTargetUsable, energy: deliveryEnergyKnown?deliveryEnergy:null, protein: deliveryProteinKnown?deliveryProtein:null,knownEnergySubtotal:deliveryEnergy,knownProteinSubtotal:deliveryProtein,components: deliveryComponents.slice(),
      energyPct: deliveryEnergyKnown?deliveryEnergyPct:null, proteinPct: deliveryProteinKnown?deliveryProteinPct:null,
      energyShortfall: !predictiveWeekOne&&deliveryEnergyKnown?Math.max(0, finalTarget - deliveryEnergy):null,
      proteinShortfall: deliveryProteinKnown?Math.max(0, proteinMidpoint - deliveryProtein):null,
      note: deliveryNote
    },
    protocols: {
      'BMI classification': selectedLabel('mep-bmi-protocol') || (region === 'india' ? 'Asian-Indian' : 'WHO adult'),
      'IBW formula': selectedLabel('mep-ibw-protocol') || 'Devine',
      'AdjBW factor': adjustmentFactor.toFixed(2),
      'ICU progression': selectedLabel('mep-icu-protocol') || 'ESPEN-based',
      'Protein reference': selectedLabel('mep-protein-protocol') || 'Context-specific ESPEN / ASPEN',
      'Renal protocol': selectedLabel('mep-renal-protocol') || 'KDIGO / KDOQI'
    },
    references: CLINICAL_RULES ? CLINICAL_RULES.sources : {},
    flags: flags.slice(),
    comparisons: compRows.slice(),
    macros: {
      protein: blocked ? 'Withheld' : Math.round((finalTarget * macroPreset.p) / 4) + ' g (' + Math.round(finalTarget * macroPreset.p) + ' kcal)',
      fat: blocked ? 'Withheld' : Math.round((finalTarget * macroPreset.f) / 9) + ' g (' + Math.round(finalTarget * macroPreset.f) + ' kcal)',
      carbohydrate: blocked ? 'Withheld' : Math.round((finalTarget * macroPreset.c) / 4) + ' g (' + Math.round(finalTarget * macroPreset.c) + ' kcal)'
    }
  };
  window.DietXP = window.DietXP || {};
  window.DietXP.MEP = window.DietXP.MEP || {};
  window.DietXP.MEP.currentResult = clinicalResult;
  document.dispatchEvent(new CustomEvent('dietxp:mep-result', { detail: clinicalResult }));

  /* Save to localStorage - requires explicit consent - item 2 */
  var consentCheckbox = el('ic-save-consent');
  if (consentCheckbox && consentCheckbox.checked) {
    try {
      sessionStorage.setItem('dietxp_ic_last', JSON.stringify({
        REE: Math.round(REE), TDEE: Math.round(TDEE),
        finalTarget: predictiveWeekOne?null:Math.round(finalTarget), energyOperator:energyOperator, upperLimitExclusive:predictiveWeekOne?finalTarget:null, method: method,
        bmi: bmi.toFixed(1), ts: Date.now()
      }));
    } catch(e) {}
  }

  /* Scroll results into view on mobile */
  if (window.innerWidth < 860) {
    card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* [#18] Screen-reader announcement of the result */
  var srEl = el('ic-sr-announce');
  if (srEl) {
    srEl.textContent = blocked
      ? 'Energy target withheld. ' + statusTitle + '. ' + statusCopy
      : 'Result ready. ' + (tier === 1 ? 'Measured' : 'Estimated') + ' REE ' + Math.round(REE) + ' kcal per day. Daily energy '+(predictiveWeekOne?'exclusive ceiling ':'target ') + energyDisplay + ' kcal per day. Assessment status: ' + statusTitle + '.';
  }
}

/* ── 13. MACRO BREAKDOWN ────────────────────────────────── */
function updateMacros(totalKcal) {
  var preset = val('ic-macro-preset');
  var p = MACRO_PRESETS[preset] || MACRO_PRESETS.standard;
  var protG = Math.round((totalKcal * p.p) / 4);
  var fatG  = Math.round((totalKcal * p.f) / 9);
  var carbG = Math.round((totalKcal * p.c) / 4);

  setText('mc-protein', protG + 'g');
  setText('mc-fat', fatG + 'g');
  setText('mc-carb', carbG + 'g');
  setText('mc-protein-kcal', Math.round(totalKcal * p.p) + ' kcal');
  setText('mc-fat-kcal', Math.round(totalKcal * p.f) + ' kcal');
  setText('mc-carb-kcal', Math.round(totalKcal * p.c) + ' kcal');
}

el('ic-macro-preset').addEventListener('change', function() {
  var finalEl = el('ic-final-val');
  if (!finalEl || finalEl.textContent === '-') return;
  var finalKcal = parseFloat(finalEl.textContent.replace(/,/g, '')) || 0;
  if (finalKcal > 0) updateMacros(finalKcal);
});

/* ── 14. PDF EXPORT ─────────────────────────────────────── */
el('ic-pdf-btn').addEventListener('click', function() {
  if (window.DietXP && window.DietXP.MEP && window.DietXP.MEP.report && window.DietXP.MEP.report.downloadReport) {
    window.DietXP.MEP.report.downloadReport();
    return;
  }
  var ree = el('ic-ree-val') ? el('ic-ree-val').textContent : '-';
  var tdee = el('ic-tdee-val') ? el('ic-tdee-val').textContent : '-';
  var target = el('ic-final-val') ? el('ic-final-val').textContent : '-';
  var method = el('ic-ree-sub') ? el('ic-ree-sub').textContent : '-';
  var bmiVal = el('ic-bmi-val') ? el('ic-bmi-val').textContent : '-';
  var bmiCat = el('ic-bmi-cat') ? el('ic-bmi-cat').textContent : '-';
  var qualityStatus = el('ic-quality-status-value') ? el('ic-quality-status-value').textContent : '-';
  var qualityReason = el('ic-quality-status-reason') ? el('ic-quality-status-reason').textContent : '-';
  var formulaText = el('ic-formula-expression') ? el('ic-formula-expression').textContent : '-';
  var formulaInputsText = el('ic-formula-inputs') ? el('ic-formula-inputs').textContent : '-';
  var formulaAssumptionsText = el('ic-formula-assumptions') ? el('ic-formula-assumptions').textContent : '-';
  var formulaTargetPhase = el('ic-formula-target-phase') ? el('ic-formula-target-phase').textContent : '-';
  var formulaProteinBasis = el('ic-formula-protein-basis') ? el('ic-formula-protein-basis').textContent : '-';
  var ts = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

  var content = [
    'DietXP CLINICAL METABOLIC REPORT',
    'Generated: ' + ts + ' IST',
    'Calculator: DietXP Metabolic Energy Profiler · Algorithm ' + ALGORITHM_VERSION,
    'Units: VO2/VCO2 L/min · ventilator VCO2 mL/min · weight kg · height cm · energy kcal/day',
    '══════════════════════════════════════════════',
    '',
    'PATIENT PARAMETERS',
    '──────────────────',
    'Sex: ' + (val('ic-sex') === 'M' ? 'Male' : 'Female'),
    'Age: ' + val('ic-age') + ' years',
    'Weight: ' + val('ic-weight') + ' kg',
    'Height: ' + val('ic-height') + ' cm',
    'BMI: ' + bmiVal + ' kg/m² - ' + bmiCat,
    'Dry / euvolemic weight: ' + (val('ic-dry-weight') ? val('ic-dry-weight') + ' kg' : 'not entered'),
    '',
    'METABOLIC ASSESSMENT',
    '──────────────────────',
    'REE: ' + ree + ' kcal/day',
    'Method: ' + method,
    'Formula: ' + formulaText,
    'Inputs used: ' + formulaInputsText,
    'Assumptions: ' + formulaAssumptionsText,
    'Clinical confidence: ' + qualityStatus + ' - ' + qualityReason,
    (val('ic-context') === 'icu' ? 'Energy before ICU phase adjustment: ' : 'Estimated daily energy before clinical adjustment: ') + tdee + ' kcal/day (factor: ' + (el('ic-pal-sub') ? el('ic-pal-sub').textContent : '-') + ')',
    'Final Energy Target: ' + target + ' kcal/day',
    'Target phase: ' + formulaTargetPhase,
    '',
    'PROTEIN PRESCRIPTION',
    '──────────────────────',
    (el('ic-protein-range') ? el('ic-protein-range').textContent : '-'),
    (el('ic-protein-min') ? el('ic-protein-min').textContent : ''),
    (el('ic-protein-rec') ? el('ic-protein-rec').textContent : ''),
    'Guideline: ' + (el('ic-protein-src') ? el('ic-protein-src').textContent : ''),
    'Protein calculation weight: ' + formulaProteinBasis,
    '',
    'MEASUREMENT QUALITY INPUTS',
    '──────────────────────',
    'Interface: ' + (val('ic-interface-type') || 'not specified'),
    'Duration: ' + (val('ic-meas-duration') || 'not entered') + ' min',
    'VO2 CV: ' + (val('ic-vo2-cv') || 'not entered') + '%',
    'VCO2 CV: ' + (val('ic-vco2-cv') || 'not entered') + '%',
    'FiO2: ' + (val('ic-fio2') || 'not entered'),
    'Steady state: ' + (val('ic-steady-state') || 'not assessed'),
    'Air leak: ' + (val('ic-air-leak') || 'not assessed'),
    'Calibration: ' + (val('ic-calibration') || 'not documented'),
    '',
    'MACRONUTRIENTS (' + val('ic-macro-preset') + ' preset)',
    '──────────────────────',
    'Protein: ' + (el('mc-protein') ? el('mc-protein').textContent : '-') + ' (' + (el('mc-protein-kcal') ? el('mc-protein-kcal').textContent : '-') + ')',
    'Fat:     ' + (el('mc-fat') ? el('mc-fat').textContent : '-') + ' (' + (el('mc-fat-kcal') ? el('mc-fat-kcal').textContent : '-') + ')',
    'Carbs:   ' + (el('mc-carb') ? el('mc-carb').textContent : '-') + ' (' + (el('mc-carb-kcal') ? el('mc-carb-kcal').textContent : '-') + ')',
    '',
    '══════════════════════════════════════════════',
    'CLINICAL NOTES / FLAGS',
    '──────────────────────'
  ];

  var flagNodes = document.querySelectorAll('#ic-flags-wrap .ic-flag div:last-child');
  flagNodes.forEach(function(f, i) {
    content.push((i + 1) + '. ' + f.textContent);
  });

  content.push('');
  content.push('ALGORITHM & REVIEW');
  content.push('──────────────────────');
  content.push('Algorithm version: ' + ALGORITHM_VERSION + ' · guideline review ' + GUIDELINE_REVIEW_DATE);
  content.push('Assumed RQ (VCO2 mode): ' + (val('ic-rq') || 'n/a'));
  content.push('Measurement quality: ' + qualityStatus);
  content.push('Protein dosing basis (selected): ' + (val('ic-protein-weight-basis') || 'auto'));
  content.push('Clinician review: _______________________   Signature: ____________   Date: __________');
  content.push('');
  content.push('Clinical Guidelines: ICMR-NIN 2020 | ESPEN ICU Nutrition Guideline 2023 | ASPEN/SCCM Critical Care Nutrition Guidance | IAPEN India');
  content.push('⚕ For use by qualified healthcare professionals only. Not a substitute for clinical judgment.');
  content.push('DietXP - dietxp.com | dietxp@immscion.com');

  var blob = new Blob([content.join('\n')], { type: 'text/plain;charset=utf-8' });
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a');
  a.href = url;
  a.download = 'DietXP_IC_Report_' + new Date().toISOString().slice(0,10) + '.txt';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
});

/* ── 15. COPY REPORT ─────────────────────────────────────── */
el('ic-copy-btn').addEventListener('click', function() {
  if (window.DietXP && window.DietXP.MEP && window.DietXP.MEP.report && window.DietXP.MEP.report.copyNote) {
    window.DietXP.MEP.report.copyNote();
    return;
  }
  var ree = el('ic-ree-val') ? el('ic-ree-val').textContent : '-';
  var tdee = el('ic-tdee-val') ? el('ic-tdee-val').textContent : '-';
  var target = el('ic-final-val') ? el('ic-final-val').textContent : '-';
  var method = el('ic-ree-sub') ? el('ic-ree-sub').textContent : '-';
  var qualityStatus = el('ic-quality-status-value') ? el('ic-quality-status-value').textContent : '-';
  var copyText = 'DietXP Metabolic Report\nREE: ' + ree + ' kcal/day | Method: ' + method + '\nConfidence: ' + qualityStatus + '\nEnergy before clinical adjustment: ' + tdee + ' kcal/day | Current target: ' + target + ' kcal/day';
  if (navigator.clipboard) {
    navigator.clipboard.writeText(copyText).then(function() {
      var btn = el('ic-copy-btn');
      btn.textContent = '✓ Copied!';
      setTimeout(function() {
        btn.innerHTML = '<svg width="14" height="14" fill="none" viewBox="0 0 14 14"><rect x="4" y="4" width="8" height="9" rx="1" stroke="currentColor" stroke-width="1.4"/><path d="M2 10V2h8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg> Copy Report';
      }, 2000);
    });
  }
});

/* ── 16. CALCULATE BUTTON ────────────────────────────────── */
el('ic-calculate-btn').addEventListener('click', calculate);

/* [#2] Safety-lock alternative-pathway buttons */
(function(){
  function goTab(){ var t = document.querySelector('.ic-tab-btn[data-tab="ic-data"]'); if (t) t.click(); }
  function goMode(mode){
    goTab();
    var m = document.querySelector('.ic-mode-btn[data-mode="' + mode + '"]');
    if (m) m.click();
    var sec = el('tab-ic-data');
    if (sec) sec.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  var bV = el('ic-safety-vco2');       if (bV) bV.addEventListener('click', function(){ goMode('vco2-only'); });
  var bP = el('ic-safety-predictive'); if (bP) bP.addEventListener('click', function(){ goMode('predictive'); });
  var bR = el('ic-safety-review');     if (bR) bR.addEventListener('click', function(){
    if (val('mep-refeeding-risk') === 'high') {
      var modifiers = el('mep-step-4');
      if (modifiers) modifiers.scrollIntoView({ behavior: 'smooth', block: 'start' });
      var refeeding = el('mep-refeeding-risk'); if (refeeding) refeeding.focus();
      return;
    }
    if (numVal('ic-age') < 18) {
      var patientStep = el('mep-step-1');
      if (patientStep) patientStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    goTab();
    var q = el('ic-quality-wrap'); if (q) q.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();

/* Enter key on inputs */
document.querySelectorAll('.ic-input').forEach(function(input) {
  input.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') calculate();
  });
});

/* ── 17. RESET ───────────────────────────────────────────── */
el('ic-reset-btn').addEventListener('click', function() {
  /* Clear all inputs and selects */
  document.querySelectorAll('.ic-input').forEach(function(i) { i.value = ''; i.classList.remove('error'); });
  document.querySelectorAll('.ic-select').forEach(function(s) { s.selectedIndex = 0; });
  /* Restore RQ defaults */
  el('ic-rq').value = '0.86';
  el('ic-rq-vo2only').value = '0.85';
  /* Uncheck protocol disturbances */
  document.querySelectorAll('#ic-protocol-checklist input').forEach(function(cb) { cb.checked = false; });
  /* Restore mode switcher to predictive default (#5) */
  document.querySelectorAll('.ic-mode-btn').forEach(function(b) { b.classList.remove('active'); });
  document.querySelectorAll('.ic-mode-section').forEach(function(s) { s.classList.remove('active'); });
  var defBtn = document.querySelector('[data-mode="predictive"]');
  if (defBtn) defBtn.classList.add('active');
  var defSection = el('mode-predictive');
  if (defSection) defSection.classList.add('active');
  /* Predictive pathway hides the measurement-quality panel */
  var qualityWrap = el('ic-quality-wrap');
  if (qualityWrap) qualityWrap.style.display = 'none';
  /* Restore activity-wrap opacity */
  var actWrap = el('activity-wrap');
  if (actWrap) actWrap.style.opacity = '1';
  /* Hide results, show placeholder */
  el('ic-results-card').classList.remove('visible');
  el('ic-results-card').style.display = 'none';
  el('ic-results-placeholder').style.display = '';
  /* Collapse measured-vs-predicted and ICMR band */
  var mvpPanel = el('ic-mvp-panel');
  if (mvpPanel) mvpPanel.classList.remove('visible');
  var icmrBand = el('ic-icmr-band');
  if (icmrBand) icmrBand.classList.remove('visible');
  /* Clear validation errors */
  document.querySelectorAll('.ic-field-error').forEach(function(e) { e.classList.remove('visible'); });
  /* Refresh previews + a11y state */
  updateBMIPreview();
  updateWeightBasis();
  updateVcoMultPreview();
  syncModeFieldA11y();
  updateLiveSummary();
});

/* ── 18. SESSION RESTORE - requires consent ────────────────── */
/* No automatic restore. Data is session-scoped and only saved when the
   user explicitly ticks the privacy consent checkbox (item 2/3 v1.4). */

/* ── 18b. REGION AUTO-SELECT FROM LIQUID DATA BRIDGE ─────── */
/* Liquid already sets the 'selected' attribute via metafield for logged-in India
   customers, so this is a JS-only fallback for sessions where the metafield is
   blank but the bridge reports 'india' (e.g. session cookie or shop locale). */
(function() {
  try {
    var bridge = window.DietXP && window.DietXP.ICCalculator;
    if (bridge && bridge.customerRegion === 'india') {
      var regionSel = el('ic-region');
      if (regionSel && regionSel.value !== 'india') {
        regionSel.value = 'india';
        updateBMIPreview();
      }
    }
  } catch(e) {}
})();

/* ── 19. INTERSECTION OBSERVER (results reveal animation) ── */
var io = new IntersectionObserver(function(entries) {
  entries.forEach(function(entry) {
    if (entry.isIntersecting) {
      entry.target.style.opacity = '1';
      entry.target.style.transform = 'translateY(0)';
    }
  });
}, { threshold: 0.1 });

document.querySelectorAll('.ic-tier-item').forEach(function(item, i) {
  item.style.opacity = '0';
  item.style.transform = 'translateY(12px)';
  item.style.transition = 'opacity .4s ease ' + (i * 0.08) + 's, transform .4s ease ' + (i * 0.08) + 's';
  io.observe(item);
});

})(); /* end IIFE */
